export const OPTIONS_LANGUAGE_STORAGE_KEY = 'geni_options_language';

export const optionsTranslations = {
  en: {
    common: {
      options: 'Options',
      dashboard: 'Dashboard',
      loadingOptions: 'Loading GENI Options…',
      connectDeribit: 'Connect Deribit',
      viewDemo: 'View Demo Portfolio',
      exitDemo: 'Exit Demo',
      backToDashboard: 'Back to dashboard',
      back: 'Back',
      manageConnection: 'Manage connection',
      source: 'Source',
      environment: 'Environment',
      view: 'View',
      period: 'Period',
      currency: 'Currency',
      underlying: 'Underlying',
      strategy: 'Strategy',
      sort: 'Sort',
      status: 'Status',
      start: 'Start',
      end: 'End',
      apply: 'Apply',
      syncNow: 'Sync now',
      syncingNow: 'Syncing…',
      production: 'Live Account',
      testnet: 'Testnet',
      all: 'ALL',
      ranges: { custom: 'Custom' },
      signedInAs: 'Signed in as',
      plan: 'Plan',
      billing: 'Billing',
      referral: 'Referral',
      settings: 'Settings',
      support: 'Support',
      terms: 'Terms',
      privacy: 'Privacy',
      logOut: 'Log out',
      demoPortfolio: 'DEMO PORTFOLIO',
      demoDataset: 'DEMONSTRATIVE DATASET',
      readOnly: 'Read only',
      realTestnet: 'REAL TESTNET',
      connectMyAccount: 'Connect your Deribit account',
      optionsStrategy: 'Options Strategy',
      hedge: 'Hedge',
      directional: 'Directional',
      directionalOverlay: 'Directional Overlay',
      portfolioBalance: 'Portfolio Balance',
      directionalSpot: 'Directional Spot',
      unclassified: 'Unclassified',
      optionInstrument: 'Option',
      futureInstrument: 'Future',
      perpetualInstrument: 'Perpetual',
      spotInstrument: 'Spot',
      complete: 'Complete',
      partial: 'Partial',
      unavailable: 'Unavailable',
      roleOverride: 'Role Override',
      followAutoRole: 'Follow auto classification',
    },
    nav: {
      dashboard: 'Dashboard',
      openPositions: 'Open Positions',
      outlook: 'BTC Outlook',
      history: 'History',
      analytics: 'Analytics',
      performance: 'Performance',
      holdings: 'Holdings',
      alerts: 'Alerts',
      about: 'About',
      viewDemo: 'View Demo Portfolio',
      exitDemo: 'Exit Demo Portfolio',
      geniTrades: 'GENI Trades',
    },
    shell: {
      demoFooterTitle: 'DEMO PORTFOLIO',
      demoFooterDescription: "You're viewing sample data.",
      demoFooterPrimaryCta: 'Connect your Deribit account',
      demoFooterSecondaryCta: 'Exit Demo',
    },
    onboarding: {
      title: 'GENI Options',
      description: 'Connect your Deribit account in read-only mode and automatically track your trades, results and performance.',
      securityTitle: 'Read-only mode.',
      securityBody: 'GENI cannot place trades or move funds from your account.',
      securityDetails: 'Automatic tracking after connection',
    },
    connection: {
      connectEyebrow: 'GENI Options · Connect',
      connectedEyebrow: 'GENI Options · Connection',
      manageDeribit: 'Manage Deribit',
      manageDescription: 'Review the current status of your Deribit connection and keep the existing sync flow in one place.',
      connectedDescription: 'Your Deribit account is connected in read-only mode and ready to reuse the current synchronization flow.',
      connecting: 'Connecting...',
      syncingData: 'Syncing data...',
      connectingDescription: 'Validating and saving your Deribit connection while keeping this page mounted.',
      syncingDescription: 'Your connection is saved. GENI is now running the initial sync before opening the dashboard.',
      initialSyncFailed: 'Deribit connected, but the initial sync could not be completed.',
      retryInitialSync: 'Try again',
      connectedStatus: 'Connected',
      statusLabel: 'Status',
      environmentLabel: 'Environment',
      lastSyncLabel: 'Last sync',
      permissionsLabel: 'Permissions',
      readOnlyActive: 'Read-only active',
      notSyncedYet: 'Not synced yet',
      disconnectDeribit: 'Disconnect Deribit',
      disconnectTitle: 'Disconnect Deribit?',
      disconnectDescription: 'This will remove the current Deribit connection from this GENI workspace.',
      disconnectWarningCurrentBehavior: 'Current backend behavior also removes imported historical data from this workspace. This action is destructive in the current version.',
      cancelDisconnect: 'Cancel',
      confirmDisconnect: 'Disconnect',
      disconnecting: 'Disconnecting…',
      disconnectedToast: 'Deribit disconnected.',
      disconnectFailedToast: 'Failed to disconnect Deribit.',
    },
    deribitTutorial: {
      helpLinkLabel: 'How do I find my Deribit API credentials?',
      modalTitle: 'How to connect your Deribit account',
      modalSubtitle: 'It only takes a few minutes. Create a read-only API key on Deribit and connect it to GENI.',
      securityBadgeTitle: 'Read-only connection',
      securityIntro: 'GENI uses a read-only API to retrieve the necessary data from your account. Your key must have exactly the read permissions shown in the tutorial.',
      scopeAccountLabel: 'Account',
      scopeTradeLabel: 'Trade',
      scopeWalletLabel: 'Wallet',
      scopeBlockTradeLabel: 'Block Trade',
      scopeBlockRfqLabel: 'Block RFQ',
      scopeCustodyLabel: 'Custody',
      scopeReadValue: 'READ',
      scopeNoneValue: 'NONE',
      rejectExcessNote: 'For your security, GENI will reject API keys with permissions beyond those required.',
      cannotTradeNote: 'With this configuration, GENI cannot place orders or withdraw funds.',
      permissionsErrorTitle: 'API permissions are not compatible',
      permissionsErrorBody: 'For your security, GENI only accepts the read-only configuration shown in the tutorial. Set Account, Trade and Wallet to READ and keep the other indicated permissions set to NONE.',
      viewTutorialButton: 'View tutorial',
    },
    optionsBeta: {
      badge: 'BETA',
      title: 'GENI Options is in Beta',
      body1: 'You are participating in the Beta phase of GENI Options.',
      body2: 'During this period, some data, classifications, or features may occasionally show inconsistencies while we improve the product based on real-world usage.',
      body3: 'Your Deribit connection uses read-only permissions. GENI cannot place orders, move funds, or make withdrawals.',
      body4: 'If you find any issue, have a question, or want to suggest an improvement, please use the Feedback button.',
      checkboxLabel: 'I understand that I am using a Beta version.',
      continueButton: 'Continue',
    },
    dashboard: {
      title: 'GENI Options Dashboard',
      eyebrow: 'GENI Options · Dashboard',
      description: 'Track your trades, performance and account growth automatically — without spreadsheets or manual fee calculations.',
      connectionErrorTitle: 'Could not verify your connection',
      connectionErrorBody: 'GENI could not confirm your Deribit connection status right now. Your connection is still saved - this is likely a temporary issue.',
      syncedJustNow: 'Synced just now',
      syncedMinutesAgo: 'Synced {count} min ago',
      syncedHoursAgo: 'Synced {count}h ago',
      lastSyncAt: 'Last sync: {time}',
      accountValueSourceReal: 'Deribit Account Summary',
      accountValueSourceDemo: 'DEMO PORTFOLIO · Demonstrative dataset',
      accountValueHeadline: 'Total Account Value',
      accountValueFallback: 'Account Value',
      accountValuePartialNote: 'Partial USD valuation · excluded from total: {currencies}',
      accountValueUnavailableNote: 'USD valuation unavailable · missing: {currencies}',
      usdValuationUnavailable: 'USD valuation unavailable',
      visualizationLabel: 'Visualization',
      assetsLabel: 'Assets',
      snapshotLabel: 'Snapshot',
      accountLabel: '{currency} Account',
      accountValueLabel: '{currency} Account Value',
      availableFunds: 'Available Funds',
      marginBalance: 'Margin Balance',
      tradingPnl: 'Trading P&L',
      tradingPerformanceLabel: 'Trading Performance',
      tradingPnlTotalLabel: 'Trading P&L',
      resultByAssetLabel: 'Result by Asset',
      noTradingResultForRange: 'No trading result in this range.',
      sinceLastCapitalChange: 'Since last capital change',
      lastCapitalEvent: 'Last event',
      capitalContextLive: 'Live current capital period',
      realized: 'Realized',
      unrealized: 'Unrealized',
      fees: 'Fees',
      openOperations: 'Open Operations',
      openOperationsHelper: 'Current positions only',
      openOperationsCta: 'View positions',
      accountEquity: 'Account Equity',
      accountEquityTitle: 'ACCOUNT EQUITY',
      accountEquityTitleBtc: 'Account Equity · BTC equivalent',
      accountEquityTitleCurrency: '{currency} Account Equity',
      insufficientHistoryTitle: 'Not enough history yet.',
      insufficientHistoryDescription: 'Your account equity history will appear as new data is synchronized.',
      equityLabel: 'Equity',
      capitalBaseLabel: 'Capital Base',
      performanceLabel: 'Performance',
      resetZoom: 'Reset',
      mouseZoomHint: 'Scroll to zoom. Drag the navigator to move through the loaded range.',
      cashFlowEventsLabel: 'Cash flow events',
      granularity: {
        auto: 'AUTO',
        daily: 'DAILY',
        weekly: 'WEEKLY',
        monthly: 'MONTHLY',
        yearly: 'YEARLY',
      },
      historyBuildingTitle: 'History is still building',
      historyBuildingDescription: 'This chart becomes more useful as new account snapshots are added over time.',
    },
    analytics: {
      title: 'Analytics',
      eyebrow: 'GENI Options · Analytics',
      description: 'Inspect wallet composition, directional exposure and portfolio greeks in the detailed risk view.',
      summary: 'Overview',
      operationQuality: 'Operation Quality & Efficiency',
      operationQualityHelper: 'Metrics based on closed operations only.',
      operationProfile: 'Winners, Losers & Streaks',
      totalOperations: 'Total Operations',
      winRate: 'Win Rate',
      tradingPnl: 'Trading P&L',
      averagePnl: 'Avg P&L',
      cyclePnl: 'Cycle P&L',
      detailedAnalyses: 'Detailed Analytics',
      detailedAnalysesHelper: 'Open each module only when you want to go deeper into a specific slice of the data.',
      editOrder: 'Edit order',
      editOrderHelper: 'Move the analysis modules and keep your preferred sequence.',
      resetOrder: 'Reset to default',
      profitFactor: 'Profit Factor',
      profitFactorTooltip: 'Gross profit divided by gross loss. Above 1.0 means the winners outweighed the losers.',
      averageWinner: 'Average Winner',
      averageLoser: 'Average Loser',
      payoffRatio: 'Payoff Ratio',
      expectancy: 'Expectancy',
      expectancyTooltip: 'Expected result per closed operation, combining win rate and average winner/loser.',
      maxDrawdown: 'Maximum Account Drawdown',
      bestOperation: 'Best Operation',
      worstOperation: 'Worst Operation',
      longestWinStreak: 'Longest Win Streak',
      longestLossStreak: 'Longest Loss Streak',
      premiumCaptured: 'Premium Captured',
      eligibleOperations: '{count} eligible operations',
      feeImpact: 'Fee Impact',
      feeImpactSecondary: '{amount} in fees',
      entryVsExitDte: 'Entry vs Exit DTE',
      averageEntryDte: 'Avg Entry DTE',
      averageExitDte: 'Avg Exit DTE',
      dteReduction: 'Avg DTE Reduction',
      closedAt: 'Closed At',
      notEnoughData: 'Not enough data',
      performanceByStrategy: 'Performance by Strategy',
      performanceByEntryDte: 'Performance by Entry DTE',
      performanceByEntryDelta: 'Performance by Entry Delta',
      performanceByUnderlying: 'Performance by Underlying',
      performanceByHoldTime: 'Performance by Hold Time',
      positionCyclesAndRolls: 'Position Cycles & Rolls',
      portfolioGreeks: 'Portfolio Greeks',
      operations: 'Operations',
      averageShort: 'Avg',
      entryDeltaCoverage: 'Uses historical entry delta only when available.',
      cyclesSubtitle: 'Real results grouped by cycle and roll transitions.',
      totalCycles: 'Total Cycles',
      cyclesCountShort: 'Cycles',
      totalRolls: 'Total Rolls',
      cycleWinRate: 'Cycle Win Rate',
      rollsPerCycle: 'Rolls per Cycle',
      preRollPnl: 'Pre-Roll Realized P&L',
      postRollPnl: 'Post-Roll P&L',
      finalCyclePnl: 'Final Cycle P&L',
      positiveCycles: 'Positive Cycles',
      negativeCycles: 'Negative Cycles',
      improvedCycles: 'Rolls Improved Result',
      worsenedCycles: 'Rolls Worsened Result',
      recoveredCycles: 'Recovered After Roll',
      partiallyRecoveredCycles: 'Partially Recovered',
      notRecoveredCycles: 'Not Recovered',
      recoveryStatusRecovered: 'Recovered',
      recoveryStatusPartiallyRecovered: 'Partially Recovered',
      recoveryStatusNotRecovered: 'Not Recovered',
      cyclesByRollCount: 'Position Cycles by Roll Count',
      cyclesByRollCountHelper: 'Aggregate cycle statistics only. No individual operations are mixed here.',
      notEnoughDataForAnalysis: 'Not enough data yet',
      moduleSummary: {
        assetCount: '{count} assets',
        bucketCount: '{count} groups',
        cyclesMeta: '{cycles} cycles · {rolls} rolls',
        tradeResultsMeta: '{winStreak} win streak · {lossStreak} loss streak',
        entryVsExitDteMeta: 'Avg DTE reduction {value}',
      },
      groups: {
        performance: 'Performance',
        positionManagement: 'Position Management',
        timing: 'Timing',
        results: 'Results',
      },
      moduleCards: {
        performanceByAssetDescription: 'Compare realized results across each underlying with data in the selected range.',
        positionCyclesTitle: 'Position Cycles',
        positionCyclesDescription: 'Review cycle-level outcomes, roll transitions and recovery context.',
        portfolioGreeksDescription: 'Inspect aggregated delta, theta, vega and gamma by currency.',
        performanceByStrategyDescription: 'See which strategies produced the strongest or weakest closed-operation results.',
        performanceByEntryDteDescription: 'Compare outcomes by entry time-to-expiration buckets.',
        performanceByDeltaDescription: 'Review closed-operation performance by entry delta buckets.',
        performanceByDurationDescription: 'Analyze closed-operation outcomes by holding time buckets.',
        tradeResultsTitle: 'Trade Results',
        tradeResultsDescription: 'Inspect average winners, average losers, streaks and the standout operations.',
        entryVsExitDteDescription: 'Compare how far from expiration positions were opened and closed.',
        cyclesByRollCountDescription: 'See how cycle performance changes with the number of rolls involved.',
      },
      rollBucket0: '0 rolls',
      rollBucket1: '1 roll',
      rollBucket2: '2 rolls',
      rollBucket3Plus: '3+ rolls',
      cycleCurrent: 'Current Cycle',
      byCurrencyFallback: 'By underlying',
      metricInfo: {
        shared: {
          readingGuide: "Read the current number in the context of GENI's actual calculation.",
        },
        sections: {
          whatIs: 'What it is',
          howCalculated: 'How GENI calculates it',
          howInterpret: 'How to interpret it',
          yourResult: 'Your result',
        },
        profitFactor: {
          whatIs: 'Profit Factor compares the gross profit from winning operations with the gross loss from losing operations in the selected range.',
          howCalculated: 'GENI sums profitable closed operations into Gross Profit and losing closed operations into Gross Loss, then divides one by the other.',
          formula: 'Gross Profit ÷ Gross Loss',
          howInterpret: '< 1.0 means losses were larger than gains in the period. Around 1.0 is roughly break-even. > 1.0 means gains outweighed losses.',
          resultTemplate: 'Current reading: {value}.',
          resultUnavailable: 'Unavailable in this range.',
          resultUnavailableNote: 'GENI needs both winning and losing observations to produce a reliable ratio.',
          resultBelowOne: 'In this range, losses outweighed gains.',
          resultAtOne: 'In this range, gains and losses were roughly balanced.',
          resultAboveOne: 'In this range, gains outweighed losses.',
          breakdownGrossProfit: 'Gross Profit',
          breakdownGrossLoss: 'Gross Loss',
        },
        expectancy: {
          whatIs: 'Expectancy estimates how much, on average, the observed history suggests each closed operation is expected to gain or lose.',
          howCalculated: 'GENI uses the same backend formula applied to the filtered closed operations.',
          formula: '(Win Rate × Average Winner) − (Loss Rate × |Average Loser|)',
          unitNote: 'The displayed unit follows the selected valuation view whenever a single matching underlying can be isolated; otherwise GENI falls back to USD.',
          howInterpret: 'Positive expectancy points to average gains per operation in the observed history. Negative expectancy points to average losses. Zero is roughly neutral.',
          resultTemplate: 'Current reading: {value} per closed operation.',
          resultUnavailable: 'Unavailable in this range.',
          resultPositive: 'The observed history points to positive average expectancy per closed operation.',
          resultNegative: 'The observed history points to negative average expectancy per closed operation.',
          resultNeutral: 'The observed history is roughly neutral on a per-operation basis.',
        },
        payoffRatio: {
          whatIs: 'Payoff Ratio compares the average gain of winning operations with the absolute average loss of losing operations.',
          howCalculated: 'GENI divides the average winner by the absolute average loser using the filtered closed operations.',
          formula: 'Average Winner ÷ |Average Loser|',
          howInterpret: 'A value of 2.0 means the average winner gained about 2× what the average loser lost. Ratios above 1.0 mean winners are larger than losers on average.',
          resultTemplate: 'Current reading: {value}.',
          resultUnavailable: 'Unavailable in this range.',
          resultAboveOne: 'The average winner is larger than the average loser.',
          resultBelowOne: 'The average winner is smaller than the average loser.',
        },
        premiumCaptured: {
          whatIs: 'Premium Captured shows how much of the premium initially collected on eligible short option legs was ultimately retained after those legs were closed.',
          howCalculated: 'GENI only counts short legs that are CLOSED and have quantity_closed > 0. Received premium comes from the closed quantity at the entry price, or a prorated entry notional when needed. Retained premium subtracts the exit notional paid to close from that received premium.',
          formula: 'Premium Captured = Retained Premium ÷ Received Premium',
          eligibilityNote: 'Long-only operations do not contribute. Partial closes count only the closed quantity. Rolled structures enter the metric through the closed short legs inside each closed operation.',
          howInterpret: 'A result of 68% means GENI retained about 68% of the premium initially received on the eligible closed short legs.',
          resultTemplate: 'Current reading: {value} across {count} eligible operations.',
          resultNote: 'Higher percentages mean more of the originally collected premium was kept after closing the eligible short legs.',
          resultUnavailable: 'Unavailable when the filtered period has no eligible closed short premium legs.',
        },
        feeImpact: {
          whatIs: 'Fee Impact shows how much of the gross trading result was consumed by fees in the selected range.',
          howCalculated: 'GENI first reconstructs the gross trading result by adding total fees back to net trading P&L. It then divides total fees by the absolute gross trading result.',
          formula: 'Fee Impact = Total Fees ÷ |Gross Trading Result before fees|',
          howInterpret: 'The percentage answers “fees as a share of what gross result?”. Lower percentages mean fees consumed a smaller part of the gross edge generated by the operations.',
          resultTemplate: 'Current reading: {impact}. Fees totaled {fees} and the gross trading result before fees was {gross}.',
          resultNote: 'This is a cost-efficiency metric: it does not change P&L, it only explains how much of the gross result was absorbed by fees.',
          resultUnavailable: 'Unavailable when there is no gross trading result to compare against.',
          resultUnavailableNote: 'GENI hides the percentage when the denominator would be zero or not reliable.',
          breakdownFees: 'Fees',
          breakdownGross: 'Gross result before fees',
          breakdownImpact: 'Impact',
        },
        maxDrawdown: {
          whatIs: 'Maximum Account Drawdown is the largest peak-to-trough drop in the stored account equity series observed during the selected period.',
          howCalculated: 'GENI scans stored account equity snapshots from options_equity_history in chronological order, tracks each new peak, and measures the deepest subsequent decline before a later recovery or a new peak.',
          formula: 'Maximum Account Drawdown = Lowest subsequent stored account equity − Peak stored account equity, also shown as a percentage of the peak',
          snapshotNote: 'The current implementation uses only the account equity snapshots currently stored by GENI. It does not backfill synthetic historical equity before monitoring began.',
          howInterpret: 'Smaller drawdowns mean the stored account equity series suffered shallower declines. Larger drawdowns mean deeper temporary or realized drops from a prior stored peak.',
          resultTemplate: 'Current reading: {pct} from peak to trough in the stored account equity series, equivalent to {amount} in the selected valuation view.',
          resultNote: 'Coverage begins at the first stored equity snapshot available for the selected period. External cash flows are not adjusted inside this metric.',
          resultUnavailable: 'Unavailable in this range.',
          resultUnavailableNote: 'GENI needs at least two stored equity snapshots inside the selected range to compute an account peak-to-trough drop.',
        },
      },
    },
    positions: {
      title: 'Open Positions',
      eyebrow: 'GENI Options · Open Positions',
      description: 'Track your open options strategies, expirations, DTE, strikes and results in one place.',
      loading: 'Loading open positions…',
      countLabel: 'Open Positions',
      countAll: 'Showing all {count} open operations',
      countPartial: 'Showing {filtered} of {total} open operations',
      riskSummaryLabel: 'Risk Summary',
      emptyTitle: 'No open options positions',
      emptyDescription: 'New synced options positions will appear here automatically.',
      filterEmptyTitle: 'No positions match these filters',
      filterEmptyWithFilters: 'Adjust the filters to see open positions again.',
      filterEmptyWithoutFilters: 'No open positions available.',
      sortNearestExpiry: 'Nearest expiration',
      sortOpenedDate: 'Opened Date',
      sortPnlDesc: 'PNL +',
      sortPnlAsc: 'PNL −',
      sortNetDelta: 'Net Delta',
      portfolioRiskEyebrow: 'Portfolio Risk',
      portfolioRiskTitle: 'PORTFOLIO RISK',
      portfolioRiskDescription: "Understand where your portfolio's directional exposure comes from.",
      walletExposure: 'Wallet Exposure',
      walletExposureHelper: 'Exposure coming from the assets currently held in the account.',
      walletExposureLegend: 'Share of total account value by asset.',
      walletExposureDirectionalTitle: 'Directional interpretation',
      walletExposureDirectionalHelper: 'This shows how wallet holdings naturally expose the account before current positions are considered.',
      walletExposureExpand: 'Show directional view',
      walletExposureCollapse: 'Hide directional view',
      positionExposure: 'Position Exposure',
      optionsExposure: 'Options Exposure',
      futuresExposure: 'Futures Exposure',
      portfolioExposure: 'Portfolio Exposure',
      totalExposure: 'Total Exposure',
      directionalExposureLabel: 'Directional Exposure',
      biasLabel: 'Bias',
      directionalExposureTooltip: 'Directional exposure of options positions based on Net Delta relative to the currency equity.',
      biasTooltip: 'GENI classification based on current directional exposure. It is not a market forecast.',
      positionExposureHelper: 'Directional exposure generated by current positions based on Net Delta.',
      optionsExposureHelper: 'Directional exposure generated by options only, kept separate from Futures and Spot roles.',
      futuresExposureHelper: 'Equivalent directional exposure coming from current Futures / Perpetual positions.',
      positionRiskByCurrency: 'How current positions are changing the directional profile of this asset.',
      portfolioExposureHelper: 'Wallet holdings plus position Net Delta, converted into the final directional exposure of the portfolio for each asset.',
      totalExposureHelper: 'Wallet holdings + Options exposure + Futures exposure = net portfolio exposure for the asset.',
      portfolioExposureScaleHelper: 'Final directional bias after combining wallet holdings with current positions.',
      noExposureSummary: 'No exposure',
      riskSectionNoData: 'No exposure available in this view.',
      positionBias: 'Position Bias',
      positionExposureFormula: 'Demonstrative formula: Net Delta ÷ Equity',
      positionExposurePendingFormula: 'Position Exposure % pending confirmed formula',
      exposureUnavailable: 'Unavailable',
      awaitingValidation: 'Awaiting validation',
      netDelta: 'Net Delta',
      netDeltaOptions: 'Net Delta Options',
      netDeltaFutures: 'Net Delta Futures',
      netDeltaTotal: 'Net Delta Total',
      delta: 'Delta',
      gamma: 'Gamma',
      theta: 'Theta',
      vega: 'Vega',
      gammaTooltip: 'Shows how much Delta tends to change as the underlying price moves.',
      thetaTooltip: 'Estimated daily effect of time decay on the position.',
      vegaTooltip: 'Estimated sensitivity to a 1 percentage point change in implied volatility.',
      currentGreeks: 'Current Greeks',
      stronglyBearish: 'STRONGLY BEARISH',
      partiallyBearish: 'PARTIALLY BEARISH',
      bearish: 'Bearish',
      neutral: 'NEUTRAL',
      balanced: 'BALANCED',
      partiallyBullish: 'PARTIALLY BULLISH',
      bullish: 'Bullish',
      bullishBias: 'BULLISH BIAS',
      bearishBias: 'BEARISH BIAS',
      stronglyBullish: 'STRONGLY BULLISH',
      strongBearish: 'Strong Bearish',
      strongBullish: 'Strong Bullish',
      portfolioRiskLiveTitle: 'Live Deribit values',
      portfolioRiskLiveNote: 'Net Delta, Theta and Vega come directly from the backend. Position Exposure % stays unavailable until the Net Delta unit is fully confirmed for each asset.',
      portfolioRiskDemoTitle: 'Demonstrative portfolio risk',
      expiryLabel: 'EXPIRY',
      pnlLabel: 'P&L',
      currentOperation: 'Current Operation',
      positionCycleMeta: 'Position Cycle',
      quantityCompact: 'Qty.',
      size: 'Size',
      notionalSize: 'Notional / Size',
      opened: 'Opened',
      campaignTotal: 'Position Cycle P&L',
      directionalBias: 'Directional Bias',
      dteAtEntry: 'DTE at Entry',
      hideDetails: 'Hide details',
      viewDetails: 'View details',
      detailHelper: 'Legs, greeks, position cycle context, mark price and P&L breakdown',
      campaignContext: 'Position Cycle Context',
      rolled: 'ROLLED',
      campaignOperations: 'Position Cycle · {count} Operations',
      managementPositionsTitle: 'Management Positions',
      managementPositionsDescription: 'Current Futures / Perpetual positions classified separately from Options strategies.',
      managementPositionLine: '{role} · {side}',
      rolledFrom: 'Position Cycle · Rolled from {month}',
      originalStructureRolled: 'Original structure rolled into the current expiration.',
      current: 'Current',
      original: 'Original',
      legs: 'Legs',
      activeLegs: 'Active Legs',
      rollHistoryTitle: 'Rolls / Realized Legs',
      rollProfit: 'PROFITABLE ROLL',
      rollLoss: 'LOSING ROLL',
      rollNeutral: 'NEUTRAL ROLL',
      rollRealizedResult: 'Realized result',
      rollDate: 'Date',
      strike: 'Strike',
      quantity: 'Quantity',
      averageEntry: 'Average Entry',
      averageExit: 'Average Exit',
      markPrice: 'Mark Price',
      realizedPnl: 'Realized P&L',
      unrealizedPnl: 'Unrealized P&L',
      netPnl: 'Net P&L',
      contracts: 'contracts',
    },
    history: {
      title: 'Options History',
      eyebrow: 'GENI Options · History',
      description: 'Review your options structures, position cycles and rolls with clear context instead of a raw technical log.',
      loading: 'Loading options history…',
      displayLabel: 'DISPLAY',
      rollBadge: 'ROLL',
      views: {
        operations: 'All Operations',
        campaigns: 'Position Cycles',
        rolls: 'Rolls',
      },
      statuses: {
        all: 'ALL',
        open: 'OPEN',
        closed: 'CLOSED',
        partial: 'PARTIAL',
      },
      emptyOperationsTitle: 'No options history yet',
      emptyOperationsDescription: 'New synced options operations will appear here automatically.',
      emptyCampaignsTitle: 'No position cycles yet',
      emptyCampaignsDescription: 'Position cycles will appear here as original ideas and roll continuations are grouped over time.',
      emptyRollsTitle: 'No rolls detected yet',
      emptyRollsDescription: 'Recognized rolls will appear here with grouped context by strategy.',
      opened: 'Opened',
      expiration: 'Expiration',
      closeDate: 'Close Date',
      hideDetails: 'Hide details',
      viewDetails: 'View details',
      detailsHelper: 'Legs, greeks, fees, realized/unrealized split and position cycle context',
      managementActivitiesTitle: 'Management Activities',
      managementActivitiesDescription: 'Futures / Perpetual / Spot actions classified outside the Options strategy engine.',
      sold: 'Sold',
      received: 'Received',
      entryPrice: 'Entry Price',
      activityDate: 'Date',
      sortLabel: 'Sort',
      sortNewest: 'Most Recent',
      sortBiggestWin: 'Biggest Win',
      sortBiggestLoss: 'Biggest Loss',
      classificationEvidence: 'Classification evidence',
      positionCycleLink: 'Position Cycle Link',
      operationLink: 'Operation Link',
      portfolioUnderlyingLink: 'Portfolio-level hedge',
      unlinkedActivity: 'No secure linkage',
      optionsExposureBefore: 'Options Exposure Before',
      combinedExposure: 'Combined Exposure',
      activityRoleLine: '{role} · {side} · {timestamp}',
      activityDeltaLine: 'Equivalent delta {delta}',
      roleUpdated: 'Classification role updated.',
      roleUpdateFailed: 'Failed to update role.',
      loadingDetails: 'Loading operation details…',
      loadingCampaignDetails: 'Loading position cycle details…',
      campaignContext: 'Position cycle context',
      campaignContextLine: '{operations} operations · {rolls} rolls · Position cycle P&L {pnl}',
      campaignsOperationCount: '{count} operations',
      campaignsRollCount: '{count} rolls',
      legsCount: '{count} legs',
      rollLinksCount: '{count} roll links',
      campaignNetPnl: 'Position Cycle P&L',
      hideTimeline: 'Hide timeline',
      viewTimeline: 'View timeline',
      timelineHelper: 'Original idea, roll steps and current or final result',
      timelineOriginal: 'Original',
      timelineCurrent: 'Current',
      timelineRollStep: 'Roll step',
      campaignTotal: 'Position Cycle P&L',
      sepRealized: 'SEP realized',
      current: 'Current',
      campaign: 'Position Cycle P&L',
      viewPositionCycle: 'View position cycle',
      hidePositionCycle: 'Hide position cycle',
    },
    campaignExplainability: {
      historyTitle: 'Position Cycle History',
      historySubtitle: 'Follow the realized result, the roll event and the current operation without digging through technical records.',
      campaignPnl: 'Position Cycle P&L',
      realizedOnRoll: 'Realized on Roll',
      currentOperationPnl: 'Current Operation P&L',
      finalOperationPnl: 'Final Operation P&L',
      previousRealized: 'Previous Realized',
      currentOperation: 'Current Operation',
      rolled: 'ROLL',
      rollOccurred: 'Roll',
      openedAt: 'Opened',
      closedAt: 'Closed',
      summaryTitle: 'Position Cycle Summary',
      summaryEquation: 'Previous realized + current operation = position cycle P&L',
    },
    optionsAlerts: {
      pageTitle: 'Alerts',
      pageDescription: 'Automatically monitor your positions and receive alerts when important conditions are reached.',
      loading: 'Loading alerts…',
      active: 'Active',
      paused: 'Paused',
      recentlyTriggered: 'Recently Triggered',
      emailAlerts: 'Email Alerts',
      emailNotConfigured: 'Email not available',
      emailReady: 'Email delivery reuses the configured GENI sender.',
      emailDemoNotice: 'Demo mode never sends real email.',
      monitoringActive: 'Monitoring active',
      monitoringDelayed: 'Monitoring temporarily unavailable',
      monitoringConnectionRequired: 'Deribit disconnected',
      monitoringNeverChecked: 'Waiting for first automatic check',
      monitoringLastChecked: 'Last checked: {time}',
      monitoringNextCheck: 'Next automatic check: {time}',
      monitoringErrorRefresh: 'Monitoring is temporarily unavailable right now.',
      monitoringErrorConnection: 'Reconnect Deribit to keep automatic monitoring running.',
      manageTitle: 'Alert rules',
      manageDescription: 'Only supported alert types are shown here.',
      createAlert: 'Create Alert',
      editAlert: 'Edit Alert',
      dialogDescription: 'Choose the alert type, scope, condition and channels. Irrelevant fields stay hidden.',
      activeTab: 'Active',
      historyTab: 'History',
      emptyActiveTitle: 'No alert rules yet',
      emptyActiveDescription: 'Create your first rule to monitor roll windows, delta, margin or P&L without leaving GENI Options.',
      emptyConfiguredTitle: 'No alerts configured',
      emptyConfiguredDescription: 'Create your first alert to automatically monitor your positions.',
      emptyHistoryTitle: 'No triggered alerts yet',
      emptyHistoryDescription: 'Triggered alert events will appear here with timestamp, threshold and CTA.',
      emailOn: 'ON',
      emailOff: 'OFF',
      emailEnabledState: 'Email alerts enabled',
      emailDisabledState: 'Email alerts disabled',
      dataBlockedTitle: 'Data blocked',
      dataBlocked: 'Data blocked',
      markRead: 'Mark read',
      open: 'Open',
      unread: 'Unread',
      recentNotifications: 'Recent alerts',
      emptyNotifications: 'No recent alert notifications.',
      scopeOperation: 'Operation',
      scopeStrategyDefault: 'Strategy default',
      selectOperation: 'Select operation',
      selectStrategy: 'Select strategy',
      operation: 'Operation',
      strategy: 'Strategy',
      underlying: 'Underlying',
      currency: 'Currency',
      type: 'Alert type',
      scope: 'Scope',
      mode: 'Mode',
      exposureThreshold: 'Exposure threshold',
      biasState: 'Bias state',
      targetBias: 'Target bias',
      direction: 'Direction',
      threshold: 'Threshold',
      thresholdDte: 'DTE threshold',
      thresholdPercent: 'Threshold (%)',
      legScope: 'Leg scope',
      unit: 'Unit',
      unitNative: 'Native',
      target: 'Target',
      metric: 'Metric',
      channels: 'Channels',
      cancel: 'Cancel',
      save: 'Save',
      savedToast: 'Alert saved.',
      pausedToast: 'Alert paused.',
      activatedToast: 'Alert activated.',
      deletedToast: 'Alert deleted.',
      markedReadToast: 'Alert marked as read.',
      preferencesSavedToast: 'Alert preferences updated.',
      emailDisabledHelper: 'Enable global email alerts first.',
      nextExpiry: 'Next expiry',
      lastTriggered: 'Triggered recently',
      blockedPremiumCaptured: 'Premium Captured is prepared for alerts, but the current backend does not expose a validated live operation metric for it yet.',
      bias: {
        neutral: 'Neutral',
        partially_bullish: 'Partially Bullish',
        bullish: 'Bullish',
        strongly_bullish: 'Strongly Bullish',
        partially_bearish: 'Partially Bearish',
        bearish: 'Bearish',
        strongly_bearish: 'Strongly Bearish',
      },
      legScopes: {
        any_short: 'Any short leg',
        short_call: 'Short call',
        short_put: 'Short put',
        any_long: 'Any long leg',
      },
      strikeTargets: {
        any_short: 'Any short strike',
        short_call: 'Short call strike',
        short_put: 'Short put strike',
      },
      marginMetrics: {
        available_funds_native: 'Available funds',
        maintenance_margin_ratio: 'Maintenance margin / equity',
        close_out_margin_ratio: 'Close-out margin / equity',
      },
      quickDialogDescription: 'This quick panel configures the most useful operation alerts without cluttering the card itself.',
      quickRows: {
        dte: 'DTE / Roll Window (days)',
        deltaPut: 'Short Put Delta',
        deltaCall: 'Short Call Delta',
        profit: 'Profit Target ({currency})',
        maxLoss: 'Max Loss / P&L ({currency})',
        strike: 'Strike Proximity (%)',
      },
      deltaHelperText: 'Triggers when the absolute delta of the short leg reaches or exceeds this value.',
      types: {
        dte_roll_window: { label: 'DTE / Roll Window' },
        iv_rank: { label: 'IV Rank' },
        iv_percentile: { label: 'IV Percentile' },
        leg_delta: { label: 'Leg Delta' },
        position_exposure: { label: 'Position Exposure / Net Delta' },
        profit_target: { label: 'Profit Target' },
        premium_captured: { label: 'Premium Captured' },
        max_loss: { label: 'Max Loss / P&L' },
        strike_proximity: { label: 'Strike Proximity' },
        breakeven_proximity: { label: 'Breakeven Proximity' },
        margin_risk: { label: 'Margin / Account Risk' },
      },
    },
    optionsFeedback: {
      button: 'Feedback',
      title: 'Send feedback',
      description: 'Help us improve GENI Options.',
      messageLabel: 'Tell us what happened',
      submit: 'Send feedback',
      sending: 'Sending…',
      successTitle: 'Feedback sent',
      successDescription: 'Thank you for helping improve GENI Options.',
      successToast: 'Feedback sent.',
      categories: {
        bug: {
          title: 'I found a problem',
          description: 'Something did not work as expected.',
        },
        suggestion: {
          title: 'I have a suggestion',
          description: 'An idea to improve GENI Options.',
        },
        question: {
          title: "I didn't understand something",
          description: 'Something in the interface, data or metrics was not clear.',
        },
      },
      placeholders: {
        bug: 'Describe what happened and what you expected to happen.',
        suggestion: 'Share your idea or what could work better.',
        question: 'Tell us what was not clear.',
      },
      errors: {
        messageRequired: 'Please write your feedback before sending.',
        submitFailed: 'Could not send feedback. Please try again.',
      },
    },
    performance: {
      title: 'Performance',
      eyebrow: 'GENI Options · Performance',
      description: 'Track your account history, trading results and capital changes in one compact view.',
      loading: 'Loading performance…',
      currentCapitalPeriodReturn: 'Current Capital Period Return',
      feesCardDescription: 'Fees remain inside Trading P&L and never absorb deposits or withdrawals.',
      accountEquityDescription: 'Equity history uses only validated account snapshots. Cash flows are marked separately so they never look like trading profit.',
      accountEquityTooltip: 'Validated equity snapshots with cash flow markers kept separate from trading performance.',
      accountEvolutionInfoDescription: 'Use this chart to read how account equity changed over time without mixing capital events into trading results.',
      accountEvolutionInfoSections: {
        whatIsHeading: 'What this chart shows',
        whatIsBody: 'The chart shows how the account equity changed over time.',
        cashFlowsHeading: 'Cash flows',
        cashFlowsBody: 'Deposits, withdrawals and transfers appear as separate markers so you can distinguish capital changes from trading results.',
        viewHeading: 'Current view',
        usdBody: 'In USD mode, account assets are shown in USD using the prices recorded in each snapshot.',
        btcBody: 'In BTC mode, the chart shows the BTC equity path only.',
        ethBody: 'In ETH mode, the chart shows the ETH equity path only.',
      },
      projection: {
        title: 'Equity Projection',
        currentEquity: 'Current Equity',
        base: 'Base',
        annualizedReturn: 'Annualized Return',
        projected12m: '12M Projection',
        tooltipEquity: 'Projected equity',
        todayTick: 'Today',
        todayMarker: 'Today',
        insufficientTitle: 'Not enough history for projection.',
        insufficientDescription: 'The projection will become available when enough validated performance history exists.',
        availableHistory: 'Available history: {days} days',
        limitedHistoryNote: 'Estimate based on limited history ({days} days).',
        usdUnavailableTitle: 'Projection is shown in BTC or ETH view.',
        usdUnavailableDescription: 'GENI does not project future BTC/USD or ETH/USD prices in this card. Switch to BTC or ETH to view the native equity projection.',
        infoTitle: 'How does the Equity Projection work?',
        infoDescription: 'Mathematical estimate based on validated historical return.',
        infoWhatIs: 'The projection estimates how account equity could evolve if the historical return used as reference continued from today onward.',
        infoHowCalculated: 'GENI prefers the last 12 months. If that history is not available, it uses the last 3 months or, as a final fallback, the last available month and annualizes the validated return using the real period length. Deposits, withdrawals and transfers are not treated as trading profit.',
        infoNote: 'This is only a mathematical estimate based on historical performance and does not represent a guarantee of future results.',
        sourceWindows: {
          '12m': 'Last 12 months',
          '3m': 'Last 3 months',
          '1m': 'Last month',
        },
      },
      cashFlowMarkers: 'Cash Flow Markers',
      cashFlowLegend: 'Cash Flow',
      equityPointLabel: 'Equity',
      historyBuildingTitle: 'History is still building',
      historyBuildingDescription: 'New syncs will add more real snapshots before this chart becomes useful.',
      historyUnavailableTitle: 'Account equity history is not available yet.',
      historyUnavailableDescription: 'New snapshots will be added as the account is synchronized.',
      singleSnapshotTitle: 'More snapshots are needed to form a curve.',
      singleSnapshotDescription: 'The current point is shown below while the history is still building.',
      currentCapitalPeriod: 'Current Capital Period',
      boundary: 'Boundary',
      currentWord: 'Current',
      depositLabel: 'Deposit',
      withdrawalLabel: 'Withdrawal',
      transferLabel: 'Transfer',
      started: 'Started',
      startingEquity: 'Starting Equity',
      currentEquity: 'Current Equity',
      endingEquity: 'Ending Equity',
      returnLabel: 'Return',
      approxReturnLabel: 'Est. Return',
      approxReturnTitle: 'Estimated Return',
      partialEstimate: 'Partial estimate',
      returnUnavailableLabel: 'Return unavailable',
      returnNotAvailableYet: 'Return not available yet',
      returnUnavailableWhy: "Why isn't return available?",
      returnStatusLabel: 'Status',
      currentPriceLabel: 'Current price',
      equivalentLabel: 'Equivalent',
      approxTotalLabel: 'Est. Total',
      quotesUpdatedAt: 'Quotes updated: {time}',
      approxReturnExplanation: 'Estimated USD equivalent of the asset returns already calculated for the selected range, converted with the current reference prices.',
      approxReturnExplanationFlows: 'Deposits, withdrawals and transfers are not recalculated here; this value reuses the capital periods already validated by GENI.',
      approxReturnPriceNote: 'Because it uses the current price, this value can change even without new operations.',
      returnAvailableByAsset: 'Available by asset',
      startedAfter: 'Started after {type} {amount}',
      currentCapitalByAssetTitle: 'Current capital period is shown per asset',
      currentCapitalByAssetDescription: 'USD does not invent a combined return when the backend marks it as unreliable.',
      currentCapitalCardDescription: 'This card uses the validated backend period directly, without recalculating anything in the frontend.',
      switchToBtcEth: 'Switch to BTC or ETH',
      noCapitalPeriod: 'No capital period available yet',
      returnUnavailable: 'Return unavailable for this range',
      returnReasons: {
        genericUnavailable: 'There is not enough data to calculate a reliable return for this period.',
        rangeBeforeReliableHistory: 'Part of the selected range happens before the reliable capital history currently available in GENI.',
        rangeBeforeReliableHistoryAfterCashFlow: 'This capital period started on {date} after a {event}. Part of the selected range happens before GENI has reliable capital history for this asset.',
        insufficientSnapshots: 'There are not enough equity snapshots yet to calculate return safely for this period.',
        missingStartingEquity: 'GENI does not yet have a reliable starting equity for this range.',
        nonPositiveStartingEquity: 'The starting capital base for this range is not positive, so a percentage return would not be reliable.',
        cashFlowAdjustedUnavailable: 'External cash flows were detected in the selected period and GENI did not confirm a reliable adjusted return for this window.',
        cashFlowsSeparatedNote: 'Deposits, withdrawals and transfers are not treated as profit or loss. After a capital change, GENI starts a new capital period to measure return correctly.',
        noValidPeriods: 'There is no validated capital period inside the selected range yet.',
        invalidGrowthFactor: 'One of the capital-period segments produced a growth factor that is not reliable enough to display as return.',
        noCapitalPeriod: 'GENI does not have a validated capital period for this asset yet.',
      },
      effectiveStartNote: 'Return calculated since {date}',
      capitalSummaryTitle: 'Capital Summary',
      capitalMovementsTitle: 'Capital Movements',
      totalDepositedLabel: 'Total deposited',
      depositsCountLabel: '{n} deposit(s)',
      totalWithdrawnLabel: 'Total withdrawn',
      withdrawalsCountLabel: '{n} withdrawal(s)',
      currentEquityLabel: 'Current equity',
      btcCumulativeReturnLabel: 'BTC cumulative return',
      usdCumulativeReturnLabel: 'USD cumulative return',
      movementDeposit: 'Deposit',
      movementWithdrawal: 'Withdrawal',
      movementTransfer: 'Transfer',
      allCapitalPeriods: 'All periods',
      noCashFlows: 'No capital movements recorded yet.',
      liveCurrentPeriod: 'Live current capital period',
      accountChanges: 'Account Changes',
      accountChangesTitle: 'Keep cash flows separate from trading',
      capitalPeriods: 'Capital Periods',
      capitalPeriodsTitle: 'Capital Periods',
      capitalPeriodsDescription: 'Track how your capital evolved between account movements.',
      currentActivePeriod: 'Current active period',
      currentBadge: 'Current',
      currentPeriodShort: 'Current Period',
      periodLabel: 'Period {index}',
      latestChange: 'Latest Change',
      noBoundaryCashFlow: 'No external cash flow inside this boundary',
      closedBy: 'Closed by {type} {amount}',
      noCapitalPeriodsInRange: 'No capital period inside this time range yet.',
      combinedRangeReturn: 'Combined range return {value}',
      returnUnavailableMultiple: 'Return unavailable across multiple capital periods',
      tradingPnlRange: 'Trading P&L ({range})',
      externalCashFlowsRange: 'External Cash Flows ({range})',
      currentAccountValue: 'Current Account Value',
      trackedSeparately: 'Tracked separately from trading',
      tradingNote: 'Deposits, withdrawals and transfers affect account equity, but they are not counted as trading profit or loss.',
      usdReturnNote: 'Return is shown by capital period in BTC or ETH views. USD does not invent a consolidated return when the backend marks it as unreliable.',
      usdAuditNote: 'Switch to BTC or ETH to audit external cash flows inside each capital period.',
      assetsCompactLabel: 'Assets',
      metricInfo: {
        sections: {
          whatIs: 'What it is',
          howCalculated: 'How it is calculated',
        },
        currentValueLabel: 'Current value',
        returnPctLabel: 'Return',
        nativeResultLabel: 'Result',
        trading: {
          description: 'Net trading result for the selected context.',
          whatIs: 'Trading P&L shows the trading-only result for the selected context, separated from deposits, withdrawals and transfers.',
          howCalculated: 'It combines realized and unrealized trading results and subtracts the fees already associated with the selected operations.',
          formula: 'Trading P&L = Realized + Unrealized - Fees',
          note: 'External capital changes stay outside this calculation.',
        },
        realized: {
          description: 'P&L of operations already closed inside the selected context.',
          whatIs: 'P&L of operations already closed inside the selected context.',
        },
        unrealized: {
          description: 'Current P&L of positions that remain open.',
          whatIs: 'Current P&L of positions that remain open.',
          note: 'This value can change while the positions remain open.',
        },
        fees: {
          description: 'Trading fees associated with the selected operations.',
          whatIs: 'Trading fees associated with the operations included in the selected range.',
          note: 'Fees are costs and remain treated inside GENI\'s validated P&L methodology.',
        },
        return: {
          description: 'Period return relative to capital, separating deposits, withdrawals and transfers from trading performance.',
          whatIs: 'Return shows the trading result of the selected interval in relation to the validated capital base for that asset.',
          howCalculatedSingle: 'GENI uses the validated capital period that intersects the selected window without turning deposits, withdrawals or transfers into trading profit.',
          howCalculatedCombined: 'When the interval crosses multiple valid capital periods, GENI compounds those capital periods inside the selected window.',
          note: 'Deposits, withdrawals and transfers remain separated from trading performance.',
        },
        approxReturn: {
          priceUnavailable: '{asset} has a valid return for the selected range, but the current reference price is unavailable right now.',
          noReturn: '{asset} does not participate in this estimate because it has no valid return for the selected range.',
        },
      },
      connectDescription: 'Connect your Deribit account in read-only mode to automatically track your trades, results and performance.',
      demoReadonlyTitle: 'DEMO PORTFOLIO',
      demoReadonlyDescription: 'The Demo Portfolio is read-only and does not allow API connection or real sync actions.',
      adminManagedTitle: 'REAL TESTNET linked to admin',
      adminManagedDescription: 'This Deribit Testnet account is already linked securely to the admin and managed centrally in the backend. No extra credentials are needed here.',
      loadingConnectState: 'Loading connection state…',
    },
    placeholders: {
      analytics: {
        title: 'Analytics',
        description: 'The Options Analytics area will be filled later without mixing data with GENI Trades.',
        eyebrow: 'GENI Options · Analytics',
        placeholderTitle: 'Options Analytics is being prepared',
        placeholderDescription: 'This environment already has its own navigation. In a future phase it will receive options-specific analysis without reusing GENI Trades screens.',
        bullets: [
          'Strategy analytics will arrive later.',
          'No Options route points to GENI Trades screens.',
          'This shell is already isolated for the second GENI product.',
        ],
      },
      holdings: {
        title: 'Holdings',
        description: 'GENI Options will have its own holdings view without reusing the Trades product screen.',
        eyebrow: 'GENI Options · Holdings',
        placeholderTitle: 'Options Holdings reserved',
        placeholderDescription: 'This area will consolidate the holdings view for the options module with the same GENI identity, inside a separate product shell.',
        bullets: [
          'No redirect to GENI Trades Holdings.',
          'Prepared for multi-asset semantics specific to Options.',
          'Shell isolated and ready for the next visual phase.',
        ],
      },
      alerts: {
        title: 'Alerts',
        description: 'The Options alerts area stays separate from Trades and will receive its own rules later.',
        eyebrow: 'GENI Options · Alerts',
        placeholderTitle: 'Options Alerts on hold',
        placeholderDescription: 'When this area is filled, it will remain inside the dedicated GENI Options shell without leaking into the traditional journal screens.',
        bullets: [
          'Separate product, shared identity.',
          'Options routes remain isolated.',
          'Clean placeholder until the functional alerts phase.',
        ],
      },
      about: {
        title: 'About',
        description: 'GENI Options is the dedicated workspace for tracking options trades inside the GENI ecosystem.',
        eyebrow: 'GENI Options · About',
        placeholderTitle: 'A second product inside GENI',
        placeholderDescription: 'GENI Trades and GENI Options now share identity and account, but they keep their own navigation and experience to avoid mixing the classic journal with the options module.',
        bullets: [
          'Same ecosystem, separate navigation.',
          'Read-only Deribit connection for automatic tracking.',
          'No changes to the traditional journal or validated backend.',
        ],
      },
      loading: 'Loading GENI Options…',
      dashboardAction: 'Dashboard',
      connectAction: 'Manage connection',
    },
    aboutInstitutional: {
      title: 'About GENI',
      eyebrow: 'About',
      byline: 'Built by a trader, for traders.',
      geniHeading: 'GENI',
      geniParagraph1: 'GENI is a trading assistant designed to remove the repetitive and time-consuming work of tracking and organizing trades.',
      geniParagraph2: 'Instead of scattered notes and endless spreadsheets, GENI automatically organizes your trading history, results and statistics in one place — helping you understand what is working, what needs improvement and how your performance evolves over time.',
      geniParagraph3: 'Our goal is simple: make trading data easier to understand and turn your own trading history into useful information for better decisions.',
      optionsHeading: 'GENI Options',
      optionsParagraph1: 'GENI Options extends this experience to options trading.',
      optionsParagraph2: 'By connecting your Deribit account through a read-only API, GENI can automatically import and organize your options activity, including strategies, open positions, rolls, position cycles, P&L, fees and account performance — without requiring manual spreadsheets or calculations.',
      optionsParagraph3: 'Your Deribit connection remains read-only: GENI tracks and analyzes your activity, but cannot place trades or withdraw funds.',
    },
    toasts: {
      failedLoadDashboard: 'Failed to load the GENI Options dashboard',
      partialDashboardData: 'Some dashboard data could not be refreshed. Showing the latest available data.',
      failedLoadOptions: 'Failed to load GENI Options',
      failedLoadPositions: 'Failed to load open positions',
      failedLoadHistory: 'Failed to load options history',
      failedLoadOperationDetail: 'Failed to load operation detail',
      failedLoadCampaignDetail: 'Failed to load position cycle detail',
      failedLoadRollHistory: 'Failed to load roll history',
      failedLoadPerformance: 'Failed to load options performance',
      selectBothDates: 'Select both start and end dates.',
      startBeforeEnd: 'Start date must be before end date.',
      syncCompleted: 'Sync completed.',
      historySyncCompleted: 'History synced.',
      syncFailed: 'Sync failed',
      failedLoadConnect: 'Failed to load GENI Options',
      connectionFailed: 'Connection failed',
      demoReadonly: 'DEMO PORTFOLIO is read-only.',
    },
  },
  pt: {
    common: {
      options: 'Options',
      dashboard: 'Dashboard',
      loadingOptions: 'Carregando GENI Options…',
      connectDeribit: 'Conectar Deribit',
      viewDemo: 'Ver Portfólio Demo',
      exitDemo: 'Sair do Demo',
      backToDashboard: 'Voltar ao dashboard',
      back: 'Voltar',
      manageConnection: 'Gerenciar conexão',
      source: 'Fonte',
      environment: 'Ambiente',
      view: 'Visualização',
      period: 'Período',
      currency: 'Moeda',
      underlying: 'Ativo base',
      strategy: 'Estratégia',
      sort: 'Ordenar',
      status: 'Status',
      start: 'Início',
      end: 'Fim',
      apply: 'Aplicar',
      syncNow: 'Sincronizar agora',
      syncingNow: 'Sincronizando…',
      production: 'Conta Real',
      testnet: 'Testnet',
      all: 'TODOS',
      ranges: { custom: 'Personalizado' },
      signedInAs: 'Conectado como',
      plan: 'Plano',
      billing: 'Billing',
      referral: 'Indicações',
      settings: 'Configurações',
      support: 'Suporte',
      terms: 'Termos',
      privacy: 'Privacidade',
      logOut: 'Sair',
      demoPortfolio: 'PORTFÓLIO DEMO',
      demoDataset: 'DADOS DEMONSTRATIVOS',
      readOnly: 'Somente leitura',
      realTestnet: 'REAL TESTNET',
      connectMyAccount: 'Conectar minha conta Deribit',
      optionsStrategy: 'Estratégia de Opções',
      hedge: 'Hedge',
      directional: 'Direcional',
      directionalOverlay: 'Direcional Adicional',
      portfolioBalance: 'Balanceamento de Carteira',
      directionalSpot: 'Spot Direcional',
      unclassified: 'Não Classificado',
      optionInstrument: 'Option',
      futureInstrument: 'Future',
      perpetualInstrument: 'Perpetual',
      spotInstrument: 'Spot',
      complete: 'Completo',
      partial: 'Parcial',
      unavailable: 'Indisponível',
      roleOverride: 'Override de Role',
      followAutoRole: 'Seguir classificação automática',
    },
    nav: {
      dashboard: 'Dashboard',
      openPositions: 'Posições Abertas',
      outlook: 'BTC Outlook',
      history: 'Histórico',
      analytics: 'Análises',
      performance: 'Performance',
      holdings: 'Holdings',
      alerts: 'Alertas',
      about: 'Sobre',
      viewDemo: 'Ver Portfólio Demo',
      exitDemo: 'Sair do Portfólio Demo',
      geniTrades: 'GENI Trades',
    },
    shell: {
      demoFooterTitle: 'PORTFÓLIO DEMO',
      demoFooterDescription: 'Você está visualizando dados demonstrativos.',
      demoFooterPrimaryCta: 'Conectar minha conta Deribit',
      demoFooterSecondaryCta: 'Sair do Demo',
    },
    onboarding: {
      title: 'GENI Options',
      description: 'Conecte sua conta Deribit em modo somente leitura e acompanhe automaticamente suas operações, resultados e performance.',
      securityTitle: 'Modo somente leitura.',
      securityBody: 'O GENI não pode executar ordens nem movimentar fundos da sua conta.',
      securityDetails: 'Acompanhamento automático após a conexão',
    },
    connection: {
      connectEyebrow: 'GENI Options · Connect',
      connectedEyebrow: 'GENI Options · Connection',
      manageDeribit: 'Gerenciar Deribit',
      manageDescription: 'Revise o status atual da sua conexão Deribit e reutilize o fluxo existente de sincronização em um só lugar.',
      connectedDescription: 'Sua conta Deribit está conectada em modo somente leitura e pronta para reutilizar o fluxo atual de sincronização.',
      connecting: 'Conectando...',
      syncingData: 'Sincronizando dados...',
      connectingDescription: 'Validando e salvando sua conexão Deribit sem desmontar esta página.',
      syncingDescription: 'Sua conexão foi salva. O GENI está executando a sincronização inicial antes de abrir o dashboard.',
      initialSyncFailed: 'Deribit conectada, mas não foi possível concluir a sincronização inicial.',
      retryInitialSync: 'Tentar novamente',
      connectedStatus: 'Conectado',
      statusLabel: 'Status',
      environmentLabel: 'Ambiente',
      lastSyncLabel: 'Última sincronização',
      permissionsLabel: 'Permissões',
      readOnlyActive: 'Somente leitura ativo',
      notSyncedYet: 'Ainda não sincronizado',
      disconnectDeribit: 'Desconectar Deribit',
      disconnectTitle: 'Desconectar Deribit?',
      disconnectDescription: 'Isso removerá a conexão atual da Deribit deste workspace do GENI.',
      disconnectWarningCurrentBehavior: 'O comportamento atual do backend também apaga os dados históricos importados deste workspace. Esta ação é destrutiva na versão atual.',
      cancelDisconnect: 'Cancelar',
      confirmDisconnect: 'Desconectar',
      disconnecting: 'Desconectando…',
      disconnectedToast: 'Deribit desconectada.',
      disconnectFailedToast: 'Não foi possível desconectar a Deribit.',
    },
    deribitTutorial: {
      helpLinkLabel: 'Como encontrar minha API na Deribit?',
      modalTitle: 'Como conectar sua conta Deribit',
      modalSubtitle: 'Leva apenas alguns minutos. Crie uma chave de API somente para leitura na Deribit e conecte-a ao GENI.',
      securityBadgeTitle: 'Conexão somente para leitura',
      securityIntro: 'O GENI utiliza uma API read-only para consultar os dados necessários da sua conta. A chave deve possuir exatamente as permissões de leitura indicadas no tutorial.',
      scopeAccountLabel: 'Account',
      scopeTradeLabel: 'Trade',
      scopeWalletLabel: 'Wallet',
      scopeBlockTradeLabel: 'Block Trade',
      scopeBlockRfqLabel: 'Block RFQ',
      scopeCustodyLabel: 'Custody',
      scopeReadValue: 'READ',
      scopeNoneValue: 'NONE',
      rejectExcessNote: 'Por segurança, o GENI não aceita a conexão se a chave possuir permissões superiores às necessárias.',
      cannotTradeNote: 'Com essa configuração, o GENI não pode executar ordens ou realizar saques.',
      permissionsErrorTitle: 'Permissões da API incompatíveis',
      permissionsErrorBody: 'Por segurança, o GENI aceita somente a configuração read-only indicada no tutorial. Configure Account, Trade e Wallet como READ e mantenha as demais permissões indicadas como NONE.',
      viewTutorialButton: 'Ver tutorial',
    },
    optionsBeta: {
      badge: 'BETA',
      title: 'GENI Options está em Beta',
      body1: 'Você está participando da fase Beta do GENI Options.',
      body2: 'Durante este período, alguns dados, classificações ou funcionalidades podem apresentar inconsistências enquanto aprimoramos o produto com base no uso real.',
      body3: 'A conexão com a Deribit utiliza somente permissões de leitura (read-only). O GENI não pode executar ordens, movimentar fundos ou realizar saques.',
      body4: 'Se encontrar qualquer problema, dúvida ou sugestão, utilize o botão Feedback para nos avisar.',
      checkboxLabel: 'Entendi que estou utilizando uma versão Beta.',
      continueButton: 'Continuar',
    },
    dashboard: {
      title: 'Dashboard do GENI Options',
      eyebrow: 'GENI Options · Dashboard',
      description: 'Acompanhe suas operações, resultados e evolução da conta automaticamente — sem preencher planilhas ou calcular taxas manualmente.',
      connectionErrorTitle: 'Não foi possível verificar sua conexão',
      connectionErrorBody: 'O GENI não conseguiu confirmar o status da sua conexão com a Deribit agora. Sua conexão continua salva - isso deve ser temporário.',
      syncedJustNow: 'Sincronizado agora',
      syncedMinutesAgo: 'Sincronizado há {count} min',
      syncedHoursAgo: 'Sincronizado há {count}h',
      lastSyncAt: 'Última sync: {time}',
      accountValueSourceReal: 'Resumo da Conta Deribit',
      accountValueSourceDemo: 'PORTFÓLIO DEMO · Dados demonstrativos',
      accountValueHeadline: 'Valor Total da Conta',
      accountValueFallback: 'Valor da Conta',
      accountValuePartialNote: 'Valuation USD parcial · fora do total: {currencies}',
      accountValueUnavailableNote: 'Valuation USD indisponível · sem preço para: {currencies}',
      usdValuationUnavailable: 'Valuation USD indisponível',
      visualizationLabel: 'Visualização',
      assetsLabel: 'Ativos',
      snapshotLabel: 'Snapshot Atual',
      accountLabel: 'Conta {currency}',
      accountValueLabel: 'Valor da Conta {currency}',
      availableFunds: 'Fundos Disponíveis',
      marginBalance: 'Saldo de Margem',
      tradingPnl: 'Trading P&L',
      tradingPerformanceLabel: 'Performance de Trading',
      tradingPnlTotalLabel: 'Trading P&L',
      resultByAssetLabel: 'Resultado por Ativo',
      noTradingResultForRange: 'Nenhum resultado de trading neste período.',
      sinceLastCapitalChange: 'Desde a última mudança de capital',
      lastCapitalEvent: 'Último evento',
      capitalContextLive: 'Período de capital atual em andamento',
      realized: 'Realizado',
      unrealized: 'Não realizado',
      fees: 'Taxas',
      openOperations: 'Operações Abertas',
      openOperationsHelper: 'Somente posições atuais',
      openOperationsCta: 'Ver posições',
      accountEquity: 'Equity da Conta',
      accountEquityTitle: 'EVOLUÇÃO DA CONTA',
      accountEquityTitleBtc: 'Equity da Conta · Equivalente em BTC',
      accountEquityTitleCurrency: 'Equity da Conta em {currency}',
      insufficientHistoryTitle: 'Histórico insuficiente.',
      insufficientHistoryDescription: 'A evolução da conta aparecerá conforme novos dados forem sincronizados.',
      equityLabel: 'Equity',
      capitalBaseLabel: 'Capital Base',
      performanceLabel: 'Performance',
      resetZoom: 'Resetar',
      mouseZoomHint: 'Use o scroll para zoom. Arraste o navegador inferior para explorar o período carregado.',
      cashFlowEventsLabel: 'Eventos de caixa',
      granularity: {
        auto: 'AUTO',
        daily: 'DIÁRIO',
        weekly: 'SEMANAL',
        monthly: 'MENSAL',
        yearly: 'ANUAL',
      },
      historyBuildingTitle: 'O histórico ainda está sendo construído',
      historyBuildingDescription: 'Este gráfico fica mais útil conforme novos snapshots da conta são adicionados ao longo do tempo.',
    },
    analytics: {
      title: 'ANÁLISES',
      eyebrow: 'GENI Options · Análises',
      description: 'Inspecione a composição da carteira, a exposição direcional e as gregas do portfólio na visão detalhada de risco.',
      summary: 'Resumo',
      operationQuality: 'Qualidade e Eficiência das Operações',
      operationQualityHelper: 'Métricas baseadas apenas em operações fechadas.',
      operationProfile: 'Ganhadoras, Perdedoras e Sequências',
      totalOperations: 'Total de Operações',
      winRate: 'Win Rate',
      tradingPnl: 'Trading P&L',
      averagePnl: 'P&L Médio',
      cyclePnl: 'P&L do Ciclo',
      detailedAnalyses: 'Análises Detalhadas',
      detailedAnalysesHelper: 'Abra cada módulo somente quando quiser aprofundar um recorte específico dos dados.',
      editOrder: 'Editar ordem',
      editOrderHelper: 'Reorganize os módulos analíticos e mantenha sua sequência preferida.',
      resetOrder: 'Restaurar padrão',
      profitFactor: 'Profit Factor',
      profitFactorTooltip: 'Lucro bruto dividido pela perda bruta. Acima de 1,0 significa que os ganhos superaram as perdas.',
      averageWinner: 'Ganho Médio',
      averageLoser: 'Perda Média',
      payoffRatio: 'Payoff Ratio',
      expectancy: 'Expectancy',
      expectancyTooltip: 'Resultado esperado por operação fechada, combinando win rate com ganho/perda médios.',
      maxDrawdown: 'Drawdown Máximo da Conta',
      bestOperation: 'Melhor Operação',
      worstOperation: 'Pior Operação',
      longestWinStreak: 'Maior Sequência de Ganhos',
      longestLossStreak: 'Maior Sequência de Perdas',
      premiumCaptured: 'Prêmio Capturado',
      eligibleOperations: '{count} operações elegíveis',
      feeImpact: 'Impacto das Taxas',
      feeImpactSecondary: '{amount} em taxas',
      entryVsExitDte: 'DTE de Entrada vs Saída',
      averageEntryDte: 'DTE Médio de Entrada',
      averageExitDte: 'DTE Médio de Saída',
      dteReduction: 'Redução Média de DTE',
      closedAt: 'Fechada em',
      notEnoughData: 'Dados insuficientes',
      performanceByStrategy: 'Performance por Estratégia',
      performanceByEntryDte: 'Performance por DTE de Entrada',
      performanceByEntryDelta: 'Performance por Delta de Entrada',
      performanceByUnderlying: 'Performance por Ativo',
      performanceByHoldTime: 'Performance por Duração',
      positionCyclesAndRolls: 'Ciclos de Posição e Rolagens',
      portfolioGreeks: 'Gregas da Carteira',
      operations: 'Operações',
      averageShort: 'Médio',
      entryDeltaCoverage: 'Usa delta histórico de entrada somente quando disponível.',
      cyclesSubtitle: 'Resultados reais agrupados por ciclo e transições de rolagem.',
      totalCycles: 'Total de Ciclos',
      cyclesCountShort: 'Ciclos',
      totalRolls: 'Total de Rolagens',
      cycleWinRate: 'Win Rate dos Ciclos',
      rollsPerCycle: 'Rolagens por Ciclo',
      preRollPnl: 'P&L Realizado Antes da Rolagem',
      postRollPnl: 'P&L Pós-Rolagem',
      finalCyclePnl: 'P&L Final dos Ciclos',
      positiveCycles: 'Ciclos Positivos',
      negativeCycles: 'Ciclos Negativos',
      improvedCycles: 'Rolagens Melhoraram o Resultado',
      worsenedCycles: 'Rolagens Pioraram o Resultado',
      recoveredCycles: 'Recuperados Após o Roll',
      partiallyRecoveredCycles: 'Parcialmente Recuperados',
      notRecoveredCycles: 'Não Recuperados',
      recoveryStatusRecovered: 'Recuperado',
      recoveryStatusPartiallyRecovered: 'Parcialmente Recuperado',
      recoveryStatusNotRecovered: 'Não Recuperado',
      cyclesByRollCount: 'Ciclos de Posição por Quantidade de Rolagens',
      cyclesByRollCountHelper: 'Somente estatísticas agregadas de ciclos. Nenhuma operação individual é misturada aqui.',
      notEnoughDataForAnalysis: 'Dados insuficientes no momento.',
      moduleSummary: {
        assetCount: '{count} ativos',
        bucketCount: '{count} grupos',
        cyclesMeta: '{cycles} ciclos · {rolls} rolagens',
        tradeResultsMeta: '{winStreak} sequência de ganhos · {lossStreak} sequência de perdas',
        entryVsExitDteMeta: 'Redução média de DTE {value}',
      },
      groups: {
        performance: 'Performance',
        positionManagement: 'Gestão da Posição',
        timing: 'Timing',
        results: 'Resultados',
      },
      moduleCards: {
        performanceByAssetDescription: 'Compare os resultados realizados entre os ativos com dados no período selecionado.',
        positionCyclesTitle: 'Ciclos de Posição',
        positionCyclesDescription: 'Revise resultados por ciclo, transições de rolagem e contexto de recuperação.',
        portfolioGreeksDescription: 'Inspecione delta, theta, vega e gamma agregados por moeda.',
        performanceByStrategyDescription: 'Veja quais estratégias produziram os melhores ou piores resultados em operações fechadas.',
        performanceByEntryDteDescription: 'Compare resultados por faixas de DTE de entrada.',
        performanceByDeltaDescription: 'Revise a performance das operações fechadas por faixas de delta de entrada.',
        performanceByDurationDescription: 'Analise os resultados das operações fechadas por faixas de duração.',
        tradeResultsTitle: 'Resultados das Operações',
        tradeResultsDescription: 'Veja ganho médio, perda média, sequências e as operações de maior destaque.',
        entryVsExitDteDescription: 'Compare a distância até o vencimento na entrada e na saída das posições.',
        cyclesByRollCountDescription: 'Entenda como a performance dos ciclos muda conforme a quantidade de rolagens.',
      },
      rollBucket0: '0 rolagens',
      rollBucket1: '1 rolagem',
      rollBucket2: '2 rolagens',
      rollBucket3Plus: '3+ rolagens',
      cycleCurrent: 'Ciclo Atual',
      byCurrencyFallback: 'Por ativo base',
      metricInfo: {
        shared: {
          readingGuide: 'Leia o número atual no contexto do cálculo real usado pelo GENI.',
        },
        sections: {
          whatIs: 'O que é',
          howCalculated: 'Como o GENI calcula',
          howInterpret: 'Como interpretar',
          yourResult: 'Seu resultado',
        },
        profitFactor: {
          whatIs: 'Profit Factor compara o lucro bruto das operações vencedoras com a perda bruta das operações perdedoras no período selecionado.',
          howCalculated: 'O GENI soma as operações fechadas lucrativas em Lucro Bruto e as operações fechadas perdedoras em Perda Bruta, e depois divide um pelo outro.',
          formula: 'Lucro Bruto ÷ Perda Bruta',
          howInterpret: '< 1,0 significa que as perdas foram maiores do que os ganhos no período. Perto de 1,0 é aproximadamente break-even. > 1,0 significa que os ganhos superaram as perdas.',
          resultTemplate: 'Leitura atual: {value}.',
          resultUnavailable: 'Indisponível neste período.',
          resultUnavailableNote: 'O GENI precisa de observações vencedoras e perdedoras para produzir uma razão confiável.',
          resultBelowOne: 'Neste período, as perdas superaram os ganhos.',
          resultAtOne: 'Neste período, ganhos e perdas ficaram aproximadamente equilibrados.',
          resultAboveOne: 'Neste período, os ganhos superaram as perdas.',
          breakdownGrossProfit: 'Lucro Bruto',
          breakdownGrossLoss: 'Perda Bruta',
        },
        expectancy: {
          whatIs: 'Expectancy estima quanto, em média, o histórico observado sugere que cada operação fechada tende a ganhar ou perder.',
          howCalculated: 'O GENI usa exatamente a mesma fórmula de backend aplicada às operações fechadas filtradas.',
          formula: '(Win Rate × Ganho Médio) − (Loss Rate × |Perda Média|)',
          unitNote: 'A unidade exibida segue a visualização selecionada sempre que um único ativo subjacente correspondente pode ser isolado; caso contrário, o GENI usa USD.',
          howInterpret: 'Expectancy positivo aponta para ganho médio por operação no histórico observado. Expectancy negativo aponta para perda média. Zero é aproximadamente neutro.',
          resultTemplate: 'Leitura atual: {value} por operação fechada.',
          resultUnavailable: 'Indisponível neste período.',
          resultPositive: 'O histórico observado aponta para expectancy médio positivo por operação fechada.',
          resultNegative: 'O histórico observado aponta para expectancy médio negativo por operação fechada.',
          resultNeutral: 'O histórico observado está aproximadamente neutro em base por operação.',
        },
        payoffRatio: {
          whatIs: 'Payoff Ratio compara o ganho médio das operações vencedoras com a perda média absoluta das operações perdedoras.',
          howCalculated: 'O GENI divide o ganho médio pela perda média absoluta usando as operações fechadas filtradas.',
          formula: 'Ganho Médio ÷ |Perda Média|',
          howInterpret: 'Um valor de 2,0 significa que a operação vencedora média ganhou cerca de 2× o que a operação perdedora média perdeu. Razões acima de 1,0 indicam vencedoras maiores do que perdedoras, em média.',
          resultTemplate: 'Leitura atual: {value}.',
          resultUnavailable: 'Indisponível neste período.',
          resultAboveOne: 'O ganho médio é maior do que a perda média.',
          resultBelowOne: 'O ganho médio é menor do que a perda média.',
        },
        premiumCaptured: {
          whatIs: 'Prêmio Capturado mostra quanto do prêmio inicialmente coletado em pernas short elegíveis foi efetivamente retido depois que essas pernas foram fechadas.',
          howCalculated: 'O GENI conta apenas pernas short que estejam CLOSED e tenham quantity_closed > 0. O prêmio recebido vem da quantidade fechada no preço médio de entrada, ou de um entry notional proporcional quando necessário. O prêmio retido subtrai o exit notional pago para fechar desse prêmio recebido.',
          formula: 'Prêmio Capturado = Prêmio Retido ÷ Prêmio Recebido',
          eligibilityNote: 'Operações somente long não entram. Fechamentos parciais contam apenas a quantidade efetivamente fechada. Estruturas roladas entram na métrica pelas pernas short fechadas dentro de cada operação encerrada.',
          howInterpret: 'Um resultado de 68% significa que o GENI reteve aproximadamente 68% do prêmio inicialmente recebido nas pernas short fechadas elegíveis.',
          resultTemplate: 'Leitura atual: {value} em {count} operações elegíveis.',
          resultNote: 'Percentuais maiores significam que uma fatia maior do prêmio originalmente coletado foi mantida após o fechamento das pernas short elegíveis.',
          resultUnavailable: 'Indisponível quando o período filtrado não possui pernas short fechadas elegíveis com prêmio.',
        },
        feeImpact: {
          whatIs: 'Impacto das Taxas mostra quanto do resultado bruto de trading foi consumido por taxas no período selecionado.',
          howCalculated: 'O GENI primeiro reconstrói o resultado bruto de trading adicionando as taxas totais de volta ao Trading P&L líquido. Depois divide as taxas totais pelo valor absoluto desse resultado bruto.',
          formula: 'Impacto das Taxas = Taxas Totais ÷ |Resultado Bruto antes das taxas|',
          howInterpret: 'A porcentagem responde “taxas como parcela de qual resultado bruto?”. Percentuais menores significam que as taxas consumiram uma parte menor da vantagem bruta gerada pelas operações.',
          resultTemplate: 'Leitura atual: {impact}. As taxas somaram {fees} e o resultado bruto antes das taxas foi {gross}.',
          resultNote: 'Esta é uma métrica de eficiência de custos: ela não altera o P&L, apenas explica quanto do resultado bruto foi absorvido por taxas.',
          resultUnavailable: 'Indisponível quando não há resultado bruto de trading para comparar.',
          resultUnavailableNote: 'O GENI oculta a porcentagem quando o denominador seria zero ou não confiável.',
          breakdownFees: 'Taxas',
          breakdownGross: 'Resultado bruto antes das taxas',
          breakdownImpact: 'Impacto',
        },
        maxDrawdown: {
          whatIs: 'Drawdown Máximo da Conta é a maior queda pico-a-vale observada na série armazenada de equity da conta durante o período selecionado.',
          howCalculated: 'O GENI percorre snapshots de equity armazenados em options_equity_history em ordem cronológica, registra cada novo pico e mede a queda subsequente mais profunda antes de uma recuperação posterior ou de um novo pico.',
          formula: 'Drawdown Máximo da Conta = Menor equity armazenada subsequente − Equity armazenada de pico, também exibido como porcentagem do pico',
          snapshotNote: 'A implementação atual usa somente os snapshots de equity da conta já armazenados pelo GENI. O sistema não faz backfill sintético de equity histórica anterior ao início do monitoramento.',
          howInterpret: 'Drawdowns menores significam quedas mais rasas na série armazenada de equity da conta. Drawdowns maiores significam quedas temporárias ou realizadas mais profundas a partir de um pico armazenado anterior.',
          resultTemplate: 'Leitura atual: {pct} do pico ao vale na série armazenada de equity da conta, equivalente a {amount} na visualização monetária selecionada.',
          resultNote: 'A cobertura começa no primeiro snapshot de equity armazenado disponível para o período selecionado. Cash flows externos não são ajustados dentro desta métrica.',
          resultUnavailable: 'Indisponível neste período.',
          resultUnavailableNote: 'O GENI precisa de pelo menos dois snapshots históricos de equity dentro do período selecionado para calcular uma queda pico-a-vale da conta.',
        },
      },
    },
    positions: {
      title: 'Posições Abertas',
      eyebrow: 'GENI Options · Posições Abertas',
      description: 'Acompanhe suas estratégias de opções abertas, vencimentos, DTE, strikes e resultados em um só lugar.',
      loading: 'Carregando posições abertas…',
      countLabel: 'Posições Abertas',
      countAll: 'Mostrando todas as {count} operações abertas',
      countPartial: 'Mostrando {filtered} de {total} operações abertas',
      riskSummaryLabel: 'Resumo de Risco',
      emptyTitle: 'Nenhuma posição de opções aberta',
      emptyDescription: 'Novas posições de opções sincronizadas aparecerão aqui automaticamente.',
      filterEmptyTitle: 'Nenhuma posição corresponde a estes filtros',
      filterEmptyWithFilters: 'Ajuste os filtros para voltar a ver as posições abertas.',
      filterEmptyWithoutFilters: 'Nenhuma posição aberta disponível.',
      sortNearestExpiry: 'Vencimento mais próximo',
      sortOpenedDate: 'Data de abertura',
      sortPnlDesc: 'PNL +',
      sortPnlAsc: 'PNL −',
      sortNetDelta: 'Net Delta',
      portfolioRiskEyebrow: 'Portfolio Risk',
      portfolioRiskTitle: 'PORTFOLIO RISK',
      portfolioRiskDescription: 'Entenda de onde vem a exposição direcional do seu portfólio.',
      walletExposure: 'Exposição da Carteira',
      walletExposureHelper: 'Exposição proveniente dos ativos mantidos na conta.',
      walletExposureLegend: 'Participação de cada ativo no valor total da conta.',
      walletExposureDirectionalTitle: 'Interpretação direcional',
      walletExposureDirectionalHelper: 'Isto mostra como a carteira já expõe a conta antes de considerar as operações atuais.',
      walletExposureExpand: 'Mostrar leitura direcional',
      walletExposureCollapse: 'Ocultar leitura direcional',
      positionExposure: 'Exposição das Operações',
      optionsExposure: 'Exposição das Opções',
      futuresExposure: 'Exposição dos Futuros',
      portfolioExposure: 'Exposição Total',
      totalExposure: 'Exposição Total',
      directionalExposureLabel: 'Exposição Direcional',
      biasLabel: 'Viés',
      directionalExposureTooltip: 'Exposição direcional das posições de opções com base no Net Delta em relação à equity da moeda.',
      biasTooltip: 'Classificação GENI baseada na exposição direcional atual. Não representa previsão de mercado.',
      positionExposureHelper: 'Exposição direcional gerada pelas operações atuais com base no Net Delta.',
      optionsExposureHelper: 'Exposição direcional gerada apenas pelas Options, separada de Futures e Spot.',
      futuresExposureHelper: 'Exposição direcional equivalente vinda das posições atuais de Futures / Perpetual.',
      positionRiskByCurrency: 'Como as operações atuais estão alterando o perfil direcional deste ativo.',
      portfolioExposureHelper: 'Ativos da carteira mais Net Delta das operações, convertidos na exposição direcional final do portfólio para cada ativo.',
      totalExposureHelper: 'Carteira + Exposição das Options + Exposição dos Futuros = exposição total líquida do ativo.',
      portfolioExposureScaleHelper: 'Viés direcional final após combinar a carteira com as operações atuais.',
      noExposureSummary: 'Sem exposição',
      riskSectionNoData: 'Nenhuma exposição disponível nesta visualização.',
      positionBias: 'Viés da Posição',
      positionExposureFormula: 'Fórmula demonstrativa: Net Delta ÷ Equity',
      positionExposurePendingFormula: 'Position Exposure % aguardando fórmula confirmada',
      exposureUnavailable: 'Indisponível',
      awaitingValidation: 'Aguardando validação',
      netDelta: 'Net Delta',
      netDeltaOptions: 'Net Delta Options',
      netDeltaFutures: 'Net Delta Futures',
      netDeltaTotal: 'Net Delta Total',
      delta: 'Delta',
      gamma: 'Gamma',
      theta: 'Theta',
      vega: 'Vega',
      gammaTooltip: 'Mostra quanto o Delta tende a mudar com o movimento do preço do ativo.',
      thetaTooltip: 'Estimativa do efeito diário da passagem do tempo sobre a carteira.',
      vegaTooltip: 'Sensibilidade estimada para uma variação de 1 ponto percentual na volatilidade implícita.',
      currentGreeks: 'Gregas atuais',
      stronglyBearish: 'MUITO BEARISH',
      partiallyBearish: 'PARCIALMENTE BEARISH',
      bearish: 'Bearish',
      neutral: 'NEUTRO',
      balanced: 'BALANCEADO',
      partiallyBullish: 'PARCIALMENTE BULLISH',
      bullish: 'Bullish',
      bullishBias: 'VIÉS BULLISH',
      bearishBias: 'VIÉS BEARISH',
      stronglyBullish: 'MUITO BULLISH',
      strongBearish: 'Forte Bearish',
      strongBullish: 'Forte Bullish',
      portfolioRiskLiveTitle: 'Valores reais da Deribit',
      portfolioRiskLiveNote: 'Net Delta, Theta e Vega vêm diretamente do backend. O Position Exposure % permanece indisponível até a unidade do Net Delta ser confirmada com segurança para cada ativo.',
      portfolioRiskDemoTitle: 'Risco demonstrativo do portfólio',
      expiryLabel: 'VENCIMENTO',
      pnlLabel: 'P&L',
      currentOperation: 'Operação Atual',
      positionCycleMeta: 'Ciclo de Posição',
      quantityCompact: 'Qtd.',
      size: 'Tamanho',
      notionalSize: 'Notional / Tamanho',
      opened: 'Aberta em',
      campaignTotal: 'P&L do Ciclo de Posição',
      directionalBias: 'Viés Direcional',
      dteAtEntry: 'DTE na Entrada',
      hideDetails: 'Ocultar detalhes',
      viewDetails: 'Ver detalhes',
      detailHelper: 'Legs, gregas, contexto do ciclo de posição, mark price e breakdown de P&L',
      campaignContext: 'Contexto do Ciclo de Posição',
      rolled: 'ROLAGEM',
      campaignOperations: 'Ciclo de Posição · {count} Operações',
      managementPositionsTitle: 'Posições de Gestão',
      managementPositionsDescription: 'Posições atuais de Futures / Perpetual classificadas separadamente das estratégias de Options.',
      managementPositionLine: '{role} · {side}',
      rolledFrom: 'Ciclo de Posição · Rolada de {month}',
      originalStructureRolled: 'A estrutura original foi rolada para o vencimento atual.',
      current: 'Atual',
      original: 'Original',
      legs: 'Legs',
      activeLegs: 'Pernas Ativas',
      rollHistoryTitle: 'Rolagens / Pernas Realizadas',
      rollProfit: 'ROLAGEM COM LUCRO',
      rollLoss: 'ROLAGEM COM PREJUÍZO',
      rollNeutral: 'ROLAGEM NEUTRA',
      rollRealizedResult: 'Resultado realizado',
      rollDate: 'Data',
      strike: 'Strike',
      quantity: 'Quantidade',
      averageEntry: 'Preço Médio de Entrada',
      averageExit: 'Preço Médio de Saída',
      markPrice: 'Mark Price',
      realizedPnl: 'P&L Realizado',
      unrealizedPnl: 'P&L Não realizado',
      netPnl: 'P&L Líquido',
      contracts: 'contratos',
    },
    history: {
      title: 'Histórico de Options',
      eyebrow: 'GENI Options · Histórico',
      description: 'Revise suas estruturas de opções, ciclos de posição e rolagens com contexto claro em vez de um log técnico cru.',
      loading: 'Carregando histórico de options…',
      displayLabel: 'VISUALIZAÇÃO',
      rollBadge: 'ROLAGEM',
      views: {
        operations: 'Todas as Operações',
        campaigns: 'Ciclos de Posição',
        rolls: 'Rolagens',
      },
      statuses: {
        all: 'TODOS',
        open: 'ABERTA',
        closed: 'FECHADA',
        partial: 'PARCIAL',
      },
      emptyOperationsTitle: 'Ainda não há histórico de options',
      emptyOperationsDescription: 'Novas operações de options sincronizadas aparecerão aqui automaticamente.',
      emptyCampaignsTitle: 'Ainda não há ciclos de posição',
      emptyCampaignsDescription: 'Os ciclos de posição vão aparecer aqui conforme a ideia original e suas rolagens forem agrupadas ao longo do tempo.',
      emptyRollsTitle: 'Nenhuma rolagem detectada ainda',
      emptyRollsDescription: 'As rolagens reconhecidas aparecerão aqui com contexto agrupado por estratégia.',
      opened: 'Abertura',
      expiration: 'Vencimento',
      closeDate: 'Data de Fechamento',
      hideDetails: 'Ocultar detalhes',
      viewDetails: 'Ver detalhes',
      detailsHelper: 'Legs, gregas, taxas, realizado/não realizado e contexto do ciclo de posição',
      managementActivitiesTitle: 'Atividades de Gestão',
      managementActivitiesDescription: 'Ações de Futures / Perpetual / Spot classificadas fora da engine de estratégia de Options.',
      sold: 'Vendido',
      received: 'Recebido',
      entryPrice: 'Preço de Entrada',
      activityDate: 'Data',
      sortLabel: 'Ordenar',
      sortNewest: 'Mais Recente',
      sortBiggestWin: 'Maior Lucro',
      sortBiggestLoss: 'Maior Prejuízo',
      classificationEvidence: 'Evidências da classificação',
      positionCycleLink: 'Vínculo com Ciclo de Posição',
      operationLink: 'Vínculo com Operação',
      portfolioUnderlyingLink: 'Hedge ao nível do portfólio',
      unlinkedActivity: 'Sem vínculo seguro',
      optionsExposureBefore: 'Exposição das Opções Antes',
      combinedExposure: 'Exposição Combinada',
      activityRoleLine: '{role} · {side} · {timestamp}',
      activityDeltaLine: 'Delta equivalente {delta}',
      roleUpdated: 'Role de classificação atualizada.',
      roleUpdateFailed: 'Não foi possível atualizar o role.',
      loadingDetails: 'Carregando detalhes da operação…',
      loadingCampaignDetails: 'Carregando detalhes do ciclo de posição…',
      campaignContext: 'Contexto do Ciclo de Posição',
      campaignContextLine: '{operations} operações · {rolls} rolagens · P&L do ciclo de posição {pnl}',
      campaignsOperationCount: '{count} operações',
      campaignsRollCount: '{count} rolagens',
      legsCount: '{count} legs',
      rollLinksCount: '{count} links de rolagem',
      campaignNetPnl: 'P&L do Ciclo de Posição',
      hideTimeline: 'Ocultar timeline',
      viewTimeline: 'Ver timeline',
      timelineHelper: 'Ideia original, etapas do roll e resultado atual ou final',
      timelineOriginal: 'Original',
      timelineCurrent: 'Atual',
      timelineRollStep: 'Etapa do roll',
      campaignTotal: 'P&L do Ciclo de Posição',
      sepRealized: 'SEP realizado',
      current: 'Atual',
      campaign: 'P&L do Ciclo de Posição',
      viewPositionCycle: 'Ver ciclo de posição',
      hidePositionCycle: 'Ocultar ciclo de posição',
    },
    campaignExplainability: {
      historyTitle: 'Histórico do Ciclo de Posição',
      historySubtitle: 'Acompanhe o resultado realizado, o evento de rolagem e a operação atual sem precisar ler registros técnicos.',
      campaignPnl: 'P&L do Ciclo de Posição',
      realizedOnRoll: 'Resultado Realizado na Rolagem',
      currentOperationPnl: 'P&L da Operação Atual',
      finalOperationPnl: 'P&L Final da Operação',
      previousRealized: 'Resultado Anterior',
      currentOperation: 'Operação Atual',
      rolled: 'ROLAGEM',
      rollOccurred: 'Rolagem',
      openedAt: 'Abertura',
      closedAt: 'Fechamento',
      summaryTitle: 'Resumo do Ciclo de Posição',
      summaryEquation: 'Resultado anterior + operação atual = P&L do ciclo de posição',
    },
    optionsAlerts: {
      pageTitle: 'Alertas',
      pageDescription: 'Monitore automaticamente suas posições e receba avisos quando condições importantes forem atingidas.',
      loading: 'Carregando alertas…',
      active: 'Ativos',
      paused: 'Pausados',
      recentlyTriggered: 'Disparados recentemente',
      emailAlerts: 'Alertas por Email',
      emailNotConfigured: 'Email indisponível',
      emailReady: 'O envio por email reutiliza o remetente já configurado do GENI.',
      emailDemoNotice: 'No modo Demo, nenhum email real é enviado.',
      monitoringActive: 'Monitoramento ativo',
      monitoringDelayed: 'Monitoramento temporariamente indisponível',
      monitoringConnectionRequired: 'Deribit desconectada',
      monitoringNeverChecked: 'Aguardando a primeira verificação automática',
      monitoringLastChecked: 'Última verificação: {time}',
      monitoringNextCheck: 'Próxima verificação automática: {time}',
      monitoringErrorRefresh: 'O monitoramento está temporariamente indisponível no momento.',
      monitoringErrorConnection: 'Reconecte a Deribit para manter o monitoramento automático funcionando.',
      manageTitle: 'Regras de alerta',
      manageDescription: 'Somente tipos de alerta realmente suportados aparecem aqui.',
      createAlert: 'Criar Alerta',
      editAlert: 'Editar Alerta',
      dialogDescription: 'Escolha o tipo de alerta, escopo, condição e canais. Campos irrelevantes ficam ocultos.',
      activeTab: 'Ativos',
      historyTab: 'Histórico',
      emptyActiveTitle: 'Nenhuma regra de alerta ainda',
      emptyActiveDescription: 'Crie sua primeira regra para monitorar janela de rolagem, delta, margem ou P&L sem sair do GENI Options.',
      emptyConfiguredTitle: 'Nenhum alerta configurado',
      emptyConfiguredDescription: 'Crie seu primeiro alerta para monitorar automaticamente suas posições.',
      emptyHistoryTitle: 'Nenhum alerta disparado ainda',
      emptyHistoryDescription: 'Os eventos disparados aparecerão aqui com horário, threshold e CTA.',
      emailOn: 'ON',
      emailOff: 'OFF',
      emailEnabledState: 'Alertas por email ativados',
      emailDisabledState: 'Alertas por email desativados',
      dataBlockedTitle: 'Dados bloqueados',
      dataBlocked: 'Dados bloqueados',
      markRead: 'Marcar como lido',
      open: 'Abrir',
      unread: 'Não lido',
      recentNotifications: 'Alertas recentes',
      emptyNotifications: 'Nenhuma notificação recente de alerta.',
      scopeOperation: 'Operação',
      scopeStrategyDefault: 'Default da estratégia',
      selectOperation: 'Selecionar operação',
      selectStrategy: 'Selecionar estratégia',
      operation: 'Operação',
      strategy: 'Estratégia',
      underlying: 'Ativo',
      currency: 'Moeda',
      type: 'Tipo de alerta',
      scope: 'Escopo',
      mode: 'Modo',
      exposureThreshold: 'Threshold de exposição',
      biasState: 'Estado de viés',
      targetBias: 'Viés alvo',
      direction: 'Direção',
      threshold: 'Threshold',
      thresholdDte: 'Threshold de DTE',
      thresholdPercent: 'Threshold (%)',
      legScope: 'Escopo da perna',
      unit: 'Unidade',
      unitNative: 'Nativo',
      target: 'Alvo',
      metric: 'Métrica',
      channels: 'Canais',
      cancel: 'Cancelar',
      save: 'Salvar',
      savedToast: 'Alerta salvo.',
      pausedToast: 'Alerta pausado.',
      activatedToast: 'Alerta ativado.',
      deletedToast: 'Alerta excluído.',
      markedReadToast: 'Alerta marcado como lido.',
      preferencesSavedToast: 'Preferências de alerta atualizadas.',
      emailDisabledHelper: 'Ative primeiro os alertas globais por email.',
      nextExpiry: 'Próximo vencimento',
      lastTriggered: 'Disparado recentemente',
      blockedPremiumCaptured: 'Premium Captured está preparado para alertas, mas o backend atual ainda não expõe uma métrica validada e ao vivo por operação para ele.',
      bias: {
        neutral: 'Neutral',
        partially_bullish: 'Parcialmente Bullish',
        bullish: 'Bullish',
        strongly_bullish: 'Fortemente Bullish',
        partially_bearish: 'Parcialmente Bearish',
        bearish: 'Bearish',
        strongly_bearish: 'Fortemente Bearish',
      },
      legScopes: {
        any_short: 'Qualquer short leg',
        short_call: 'Short call',
        short_put: 'Short put',
        any_long: 'Qualquer long leg',
      },
      strikeTargets: {
        any_short: 'Qualquer short strike',
        short_call: 'Strike da short call',
        short_put: 'Strike da short put',
      },
      marginMetrics: {
        available_funds_native: 'Fundos disponíveis',
        maintenance_margin_ratio: 'Maintenance margin / equity',
        close_out_margin_ratio: 'Close-out margin / equity',
      },
      quickDialogDescription: 'Este painel rápido configura os alertas mais úteis da operação sem poluir o card da posição.',
      quickRows: {
        dte: 'DTE / Janela de Rolagem (dias)',
        deltaPut: 'Delta da Put vendida',
        deltaCall: 'Delta da Call vendida',
        profit: 'Profit Target ({currency})',
        maxLoss: 'Max Loss / P&L ({currency})',
        strike: 'Proximidade do strike (%)',
      },
      deltaHelperText: 'Dispara quando o delta absoluto da leg vendida atingir ou ultrapassar este valor.',
      types: {
        dte_roll_window: { label: 'DTE / Janela de Rolagem' },
        iv_rank: { label: 'IV Rank' },
        iv_percentile: { label: 'IV Percentile' },
        leg_delta: { label: 'Delta da Perna' },
        position_exposure: { label: 'Position Exposure / Net Delta' },
        profit_target: { label: 'Profit Target' },
        premium_captured: { label: 'Premium Captured' },
        max_loss: { label: 'Max Loss / P&L' },
        strike_proximity: { label: 'Strike Proximity' },
        breakeven_proximity: { label: 'Breakeven Proximity' },
        margin_risk: { label: 'Margin / Account Risk' },
      },
    },
    optionsFeedback: {
      button: 'Feedback',
      title: 'Enviar feedback',
      description: 'Ajude a melhorar o GENI Options.',
      messageLabel: 'Conte o que aconteceu',
      submit: 'Enviar feedback',
      sending: 'Enviando...',
      successTitle: 'Feedback enviado',
      successDescription: 'Obrigado por ajudar a melhorar o GENI Options.',
      successToast: 'Feedback enviado.',
      categories: {
        bug: {
          title: 'Encontrei um problema',
          description: 'Algo não funcionou como esperado.',
        },
        suggestion: {
          title: 'Tenho uma sugestão',
          description: 'Uma ideia para melhorar o GENI Options.',
        },
        question: {
          title: 'Não entendi alguma coisa',
          description: 'Algo na interface, nos dados ou nas métricas não ficou claro.',
        },
      },
      placeholders: {
        bug: 'Descreva o que aconteceu e o que você esperava que acontecesse.',
        suggestion: 'Conte sua ideia ou o que poderia funcionar melhor.',
        question: 'Conte o que não ficou claro.',
      },
      errors: {
        messageRequired: 'Escreva seu feedback antes de enviar.',
        submitFailed: 'Não foi possível enviar o feedback. Tente novamente.',
      },
    },
    performance: {
      title: 'Performance',
      eyebrow: 'GENI Options · Performance',
      description: 'Acompanhe o histórico da conta, o resultado de trading e as mudanças de capital em uma visão compacta.',
      loading: 'Carregando performance…',
      currentCapitalPeriodReturn: 'Retorno do Período de Capital Atual',
      feesCardDescription: 'As taxas permanecem dentro do Trading P&L e nunca absorvem depósitos ou saques.',
      accountEquityDescription: 'O histórico de equity usa apenas snapshots validados da conta. Os cash flows são marcados separadamente para nunca parecerem lucro de trading.',
      accountEquityTooltip: 'Snapshots validados de equity com marcadores de cash flow separados da performance de trading.',
      accountEvolutionInfoDescription: 'Use este gráfico para ler como a equity da conta mudou ao longo do tempo sem misturar eventos de capital ao resultado de trading.',
      accountEvolutionInfoSections: {
        whatIsHeading: 'O que este gráfico mostra',
        whatIsBody: 'O gráfico mostra como a equity da conta mudou ao longo do tempo.',
        cashFlowsHeading: 'Cash flows',
        cashFlowsBody: 'Depósitos, saques e transferências aparecem como marcadores separados para diferenciar mudanças de capital do resultado das operações.',
        viewHeading: 'Visualização atual',
        usdBody: 'No modo USD, os ativos da conta são apresentados em USD usando os preços registrados nos snapshots.',
        btcBody: 'Mostra a evolução da equity BTC.',
        ethBody: 'Mostra a evolução da equity ETH.',
      },
      projection: {
        title: 'Projeção de Equity',
        currentEquity: 'Equity Atual',
        base: 'Base',
        annualizedReturn: 'Rentabilidade Anualizada',
        projected12m: 'Projeção em 12 meses',
        tooltipEquity: 'Equity projetada',
        todayTick: 'Hoje',
        todayMarker: 'Hoje',
        insufficientTitle: 'Histórico insuficiente para projeção.',
        insufficientDescription: 'A projeção ficará disponível quando houver histórico suficiente de performance validada.',
        availableHistory: 'Histórico disponível: {days} dias',
        limitedHistoryNote: 'Estimativa baseada em histórico limitado ({days} dias).',
        usdUnavailableTitle: 'A projeção é mostrada nas visualizações BTC ou ETH.',
        usdUnavailableDescription: 'O GENI não projeta preços futuros de BTC/USD ou ETH/USD neste card. Mude para BTC ou ETH para ver a projeção nativa da equity.',
        infoTitle: 'Como funciona a Projeção de Equity?',
        infoDescription: 'Estimativa matemática baseada na rentabilidade histórica validada.',
        infoWhatIs: 'A projeção estima como a equity da conta poderia evoluir se a rentabilidade histórica usada como referência continuasse a partir de hoje.',
        infoHowCalculated: 'O GENI utiliza preferencialmente os últimos 12 meses. Quando esse histórico não está disponível, usa os últimos 3 meses ou, como último fallback, o último mês disponível e anualiza a rentabilidade validada usando a duração real do período. Depósitos, saques e transferências não são tratados como lucro de trading.',
        infoNote: 'A projeção é apenas uma estimativa matemática baseada no histórico e não representa garantia de resultados futuros.',
        sourceWindows: {
          '12m': 'Últimos 12 meses',
          '3m': 'Últimos 3 meses',
          '1m': 'Último mês',
        },
      },
      cashFlowMarkers: 'Marcadores de Cash Flow',
      cashFlowLegend: 'Cash Flow',
      equityPointLabel: 'Equity',
      historyBuildingTitle: 'O histórico ainda está sendo construído',
      historyBuildingDescription: 'Novas sincronizações vão adicionar snapshots reais antes de este gráfico ficar realmente útil.',
      historyUnavailableTitle: 'Histórico de equity ainda não disponível.',
      historyUnavailableDescription: 'Novos snapshots serão adicionados conforme a conta for sincronizada.',
      singleSnapshotTitle: 'Mais snapshots são necessários para formar uma curva.',
      singleSnapshotDescription: 'O ponto atual é exibido enquanto o histórico ainda está sendo construído.',
      currentCapitalPeriod: 'Período de Capital Atual',
      boundary: 'Fronteira',
      currentWord: 'Atual',
      depositLabel: 'Depósito',
      withdrawalLabel: 'Saque',
      transferLabel: 'Transferência',
      started: 'Início',
      startingEquity: 'Equity Inicial',
      currentEquity: 'Equity Atual',
      endingEquity: 'Equity Final',
      returnLabel: 'Retorno',
      approxReturnLabel: 'Retorno Aprox.',
      approxReturnTitle: 'Retorno Aproximado',
      partialEstimate: 'Estimativa parcial',
      returnUnavailableLabel: 'Retorno indisponível',
      returnNotAvailableYet: 'Retorno ainda não disponível',
      returnUnavailableWhy: 'Por que o retorno não está disponível?',
      returnStatusLabel: 'Status',
      currentPriceLabel: 'Cotação atual',
      equivalentLabel: 'Equivalente',
      approxTotalLabel: 'Total Aprox.',
      quotesUpdatedAt: 'Cotações atualizadas: {time}',
      approxReturnExplanation: 'Equivalente aproximado em USD dos retornos calculados por ativo no período selecionado, convertido pelas cotações atuais de referência.',
      approxReturnExplanationFlows: 'Depósitos, saques e transferências não são recalculados aqui; o valor utiliza os períodos de capital já calculados pelo GENI.',
      approxReturnPriceNote: 'Por utilizar a cotação atual, este valor pode mudar mesmo sem novas operações.',
      returnAvailableByAsset: 'Disponível por ativo',
      startedAfter: 'Iniciado após {type} {amount}',
      currentCapitalByAssetTitle: 'O período de capital atual é exibido por ativo',
      currentCapitalByAssetDescription: 'USD não inventa um retorno combinado quando o backend marca esse número como não confiável.',
      currentCapitalCardDescription: 'Este card usa diretamente o período validado no backend, sem recalcular nada no frontend.',
      switchToBtcEth: 'Troque para BTC ou ETH',
      noCapitalPeriod: 'Ainda não há período de capital disponível',
      returnUnavailable: 'Retorno indisponível para este período',
      returnReasons: {
        genericUnavailable: 'Não há dados suficientes para calcular um retorno confiável neste período.',
        rangeBeforeReliableHistory: 'Parte do período selecionado acontece antes do início do histórico confiável de capital disponível no GENI.',
        rangeBeforeReliableHistoryAfterCashFlow: 'Este período de capital começou em {date} após um {event}. Parte do intervalo selecionado acontece antes do histórico confiável de capital que o GENI possui para este ativo.',
        insufficientSnapshots: 'Ainda não existem snapshots suficientes de equity para calcular o retorno deste período com segurança.',
        missingStartingEquity: 'O GENI ainda não possui uma equity inicial confiável para calcular a rentabilidade deste intervalo.',
        nonPositiveStartingEquity: 'A base de capital inicial deste intervalo não é positiva, então um retorno percentual não seria confiável.',
        cashFlowAdjustedUnavailable: 'Foram detectados cash flows externos no período selecionado e o GENI não confirmou um retorno ajustado confiável para esta janela.',
        cashFlowsSeparatedNote: 'Depósitos, saques e transferências não são tratados como lucro ou prejuízo. Após uma mudança de capital, o GENI inicia um novo período de capital para medir a rentabilidade corretamente.',
        noValidPeriods: 'Ainda não existe um período de capital validado dentro do intervalo selecionado.',
        invalidGrowthFactor: 'Um dos trechos do período de capital gerou um fator de crescimento que não é confiável o suficiente para ser exibido como retorno.',
        noCapitalPeriod: 'O GENI ainda não possui um período de capital validado para este ativo.',
      },
      effectiveStartNote: 'Retorno calculado desde {date}',
      capitalSummaryTitle: 'Resumo de Capital',
      capitalMovementsTitle: 'Movimentações de Capital',
      totalDepositedLabel: 'Total depositado',
      depositsCountLabel: '{n} depósito(s)',
      totalWithdrawnLabel: 'Total sacado',
      withdrawalsCountLabel: '{n} saque(s)',
      currentEquityLabel: 'Equity atual',
      btcCumulativeReturnLabel: 'Retorno acumulado BTC',
      usdCumulativeReturnLabel: 'Retorno acumulado USD',
      movementDeposit: 'Depósito',
      movementWithdrawal: 'Saque',
      movementTransfer: 'Transferência',
      allCapitalPeriods: 'Todos os períodos',
      noCashFlows: 'Nenhuma movimentação de capital registrada.',
      liveCurrentPeriod: 'Período de capital atual em andamento',
      accountChanges: 'Mudanças na Conta',
      accountChangesTitle: 'Separe cash flows do resultado de trading',
      capitalPeriods: 'Períodos de Capital',
      capitalPeriodsTitle: 'Períodos de Capital',
      capitalPeriodsDescription: 'Acompanhe a evolução do capital entre suas movimentações.',
      currentActivePeriod: 'Período atual ativo',
      currentBadge: 'Atual',
      currentPeriodShort: 'Período Atual',
      periodLabel: 'Período {index}',
      latestChange: 'Última alteração',
      noBoundaryCashFlow: 'Nenhum cash flow externo dentro desta fronteira',
      closedBy: 'Encerrado por {type} {amount}',
      noCapitalPeriodsInRange: 'Ainda não há período de capital dentro deste intervalo.',
      combinedRangeReturn: 'Retorno combinado do intervalo {value}',
      returnUnavailableMultiple: 'Retorno indisponível entre múltiplos períodos de capital',
      tradingPnlRange: 'Trading P&L ({range})',
      externalCashFlowsRange: 'Cash Flows Externos ({range})',
      currentAccountValue: 'Valor Atual da Conta',
      trackedSeparately: 'Acompanhado separadamente do trading',
      tradingNote: 'Depósitos, saques e transferências afetam a equity da conta, mas não contam como lucro ou prejuízo de trading.',
      usdReturnNote: 'O retorno é exibido por período de capital nas visões BTC ou ETH. Em USD, o sistema não inventa um retorno consolidado quando o backend o marca como não confiável.',
      usdAuditNote: 'Troque para BTC ou ETH para auditar os cash flows externos dentro de cada período de capital.',
      assetsCompactLabel: 'Ativos',
      metricInfo: {
        sections: {
          whatIs: 'O que é',
          howCalculated: 'Como é calculado',
        },
        currentValueLabel: 'Valor atual',
        returnPctLabel: 'Retorno',
        nativeResultLabel: 'Resultado',
        trading: {
          description: 'Resultado líquido de trading dentro do contexto selecionado.',
          whatIs: 'Trading P&L mostra o resultado apenas do trading no contexto selecionado, separado de depósitos, saques e transferências.',
          howCalculated: 'Ele combina o resultado realizado e o não realizado e subtrai as taxas já associadas às operações consideradas.',
          formula: 'Trading P&L = Realizado + Não Realizado - Taxas',
          note: 'Mudanças externas de capital ficam fora desse cálculo.',
        },
        realized: {
          description: 'P&L das operações já encerradas dentro do contexto selecionado.',
          whatIs: 'P&L das operações já encerradas dentro do contexto selecionado.',
        },
        unrealized: {
          description: 'P&L atual das posições que permanecem abertas.',
          whatIs: 'P&L atual das posições que permanecem abertas.',
          note: 'Esse valor pode mudar enquanto as posições permanecerem abertas.',
        },
        fees: {
          description: 'Taxas de negociação associadas às operações consideradas.',
          whatIs: 'Taxas de negociação associadas às operações incluídas no período selecionado.',
          note: 'As taxas são custos e continuam tratadas dentro da metodologia validada de P&L do GENI.',
        },
        return: {
          description: 'Retorno do período em relação ao capital, separando depósitos, saques e transferências da performance de trading.',
          whatIs: 'O retorno mostra o resultado de trading do intervalo selecionado em relação à base de capital validada daquele ativo.',
          howCalculatedSingle: 'O GENI usa o período de capital validado que cruza a janela selecionada, sem transformar depósitos, saques ou transferências em lucro de trading.',
          howCalculatedCombined: 'Quando o intervalo cruza múltiplos períodos de capital válidos, o GENI compõe esses períodos dentro da janela selecionada.',
          note: 'Depósitos, saques e transferências permanecem separados da performance de trading.',
        },
        approxReturn: {
          priceUnavailable: '{asset} tem retorno válido no período selecionado, mas a cotação atual de referência está indisponível neste momento.',
          noReturn: '{asset} não participa desta estimativa porque não possui retorno válido no período selecionado.',
        },
      },
      connectDescription: 'Conecte sua conta Deribit em modo somente leitura para acompanhar automaticamente suas operações, resultados e performance.',
      demoReadonlyTitle: 'PORTFÓLIO DEMO',
      demoReadonlyDescription: 'O Portfólio Demo é somente leitura e não permite conectar API nem acionar sincronizações reais.',
      adminManagedTitle: 'REAL TESTNET vinculada ao admin',
      adminManagedDescription: 'Esta conta Deribit Testnet já está associada com segurança ao admin e gerenciada centralmente no backend. Nenhuma credencial adicional é necessária aqui.',
      loadingConnectState: 'Carregando estado da conexão…',
    },
    placeholders: {
      analytics: {
        title: 'Analytics',
        description: 'A área de Analytics de Options será preenchida depois, sem misturar dados com o GENI Trades.',
        eyebrow: 'GENI Options · Analytics',
        placeholderTitle: 'Analytics de Options em preparação',
        placeholderDescription: 'Este ambiente já tem navegação própria. Em uma próxima fase, ele receberá análises específicas de opções sem reaproveitar telas do GENI Trades.',
        bullets: [
          'Analytics por estratégia chega depois.',
          'Nenhuma rota de Options aponta para telas do GENI Trades.',
          'Este shell já está isolado para o segundo produto GENI.',
        ],
      },
      holdings: {
        title: 'Holdings',
        description: 'O GENI Options terá sua própria visão de holdings sem reaproveitar a tela do produto Trades.',
        eyebrow: 'GENI Options · Holdings',
        placeholderTitle: 'Holdings de Options reservado',
        placeholderDescription: 'Esta área vai consolidar a visão de holdings do módulo de opções com a mesma identidade do GENI, dentro de um shell separado.',
        bullets: [
          'Sem redirecionar para Holdings do GENI Trades.',
          'Preparado para semântica multi-asset específica de Options.',
          'Shell isolado e pronto para a próxima fase visual.',
        ],
      },
      alerts: {
        title: 'Alertas',
        description: 'A área de alertas de Options permanece separada do Trades e receberá regras próprias depois.',
        eyebrow: 'GENI Options · Alertas',
        placeholderTitle: 'Alertas de Options em espera',
        placeholderDescription: 'Quando esta área for preenchida, ela continuará dentro do shell dedicado do GENI Options sem vazar para as telas do journal tradicional.',
        bullets: [
          'Produto separado, identidade compartilhada.',
          'Rotas de Options permanecem isoladas.',
          'Placeholder limpo até a fase funcional de alertas.',
        ],
      },
      about: {
        title: 'Sobre',
        description: 'GENI Options é o ambiente dedicado para acompanhar operações de opções dentro do ecossistema GENI.',
        eyebrow: 'GENI Options · Sobre',
        placeholderTitle: 'Um segundo produto dentro do GENI',
        placeholderDescription: 'GENI Trades e GENI Options compartilham identidade e conta, mas mantêm navegação e experiência próprias para evitar misturar o journal clássico com o módulo de opções.',
        bullets: [
          'Mesmo ecossistema, navegação separada.',
          'Conexão Deribit read-only para acompanhamento automático.',
          'Sem alterar o journal tradicional nem o backend validado.',
        ],
      },
      loading: 'Carregando GENI Options…',
      dashboardAction: 'Dashboard',
      connectAction: 'Gerenciar conexão',
    },
    aboutInstitutional: {
      title: 'Sobre o GENI',
      eyebrow: 'Sobre',
      byline: 'Criado por um trader, para traders.',
      geniHeading: 'GENI',
      geniParagraph1: 'O GENI é um assistente de trading desenvolvido para eliminar o trabalho repetitivo e demorado de registrar e organizar operações manualmente.',
      geniParagraph2: 'Em vez de anotações espalhadas e planilhas intermináveis, o GENI organiza seu histórico de operações, resultados e estatísticas em um só lugar — ajudando você a entender o que está funcionando, o que precisa melhorar e como sua performance evolui ao longo do tempo.',
      geniParagraph3: 'Nosso objetivo é simples: tornar seus dados de trading mais fáceis de entender e transformar seu próprio histórico em informações úteis para tomar decisões melhores.',
      optionsHeading: 'GENI Options',
      optionsParagraph1: 'O GENI Options leva essa experiência também para o mercado de opções.',
      optionsParagraph2: 'Ao conectar sua conta Deribit através de uma API somente leitura, o GENI pode importar e organizar automaticamente suas operações de opções, incluindo estratégias, posições abertas, rolagens, ciclos de posição, P&L, taxas e performance da conta — sem precisar preencher planilhas ou fazer cálculos manualmente.',
      optionsParagraph3: 'Sua conexão com a Deribit permanece somente leitura: o GENI acompanha e analisa suas operações, mas não pode realizar trades nem retirar fundos.',
    },
    toasts: {
      failedLoadDashboard: 'Não foi possível carregar o dashboard do GENI Options',
      partialDashboardData: 'Alguns dados do dashboard não puderam ser atualizados. Exibindo os últimos dados disponíveis.',
      failedLoadOptions: 'Não foi possível carregar o GENI Options',
      failedLoadPositions: 'Não foi possível carregar as posições abertas',
      failedLoadHistory: 'Não foi possível carregar o histórico de options',
      failedLoadOperationDetail: 'Não foi possível carregar o detalhe da operação',
      failedLoadCampaignDetail: 'Não foi possível carregar o detalhe do ciclo de posição',
      failedLoadRollHistory: 'Não foi possível carregar o histórico de rolls',
      failedLoadPerformance: 'Não foi possível carregar a performance de options',
      selectBothDates: 'Selecione as duas datas.',
      startBeforeEnd: 'A data inicial deve ser anterior à data final.',
      syncCompleted: 'Sincronização concluída.',
      historySyncCompleted: 'Histórico sincronizado.',
      syncFailed: 'Falha na sincronização',
      failedLoadConnect: 'Não foi possível carregar o GENI Options',
      connectionFailed: 'Falha na conexão',
      demoReadonly: 'O PORTFÓLIO DEMO é somente leitura.',
    },
  },
};

const readValue = (source, path) => path.split('.').reduce((current, key) => current?.[key], source);

export const createOptionsTranslator = (language) => {
  const dictionary = optionsTranslations[language] || optionsTranslations.en;
  return (key, fallback = '') => readValue(dictionary, key) ?? fallback;
};

export const formatOptionsMessage = (template, values = {}) => {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));
};
